import { Fragment, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { maakQueryClient } from "./queryClient";
import { Refine, useCustom, useGetIdentity, useLogout, useTranslate } from "@refinedev/core";
import { Inlogpoort } from "./Inlogpoort";
import { TaalVanAccount } from "./TaalVanAccount";
import {
  PlusCircleSolidIcon, AppShell, Button, NavSection, NavItem, ProfileCard } from "@fawkes/ui";
import { PawPrint } from "lucide-react";
import {
  ShoppingCart,
  LayoutGrid,
  Dog,
  Users as UsersIcon,
  Brush,
  ClipboardCheck,
  Pill,
  Utensils,
  ClipboardList,
  ListChecks,
  HandHeart,
  HeartPulse,
  CalendarDays,
  Scale,
  ArrowRightLeft,
  Package,
  Boxes,
  BedDouble,
  Gift,
  Baby,
  House,
  Ambulance,
  Flame,
  Truck,
  Inbox,
  FolderOpen,
  Calendar,
  Receipt,
  MessagesSquare,
  Settings2,
  Megaphone,
  BadgePercent,
  ShieldCheck,
  UserRound,
  Building2,
  KeyRound,
  Blocks,
  Wrench,
  ScanSearch,
  Wallet,
  BarChart3,
  CreditCard,
  BookOpen,
  CalendarCheck,
  Download,
  type LucideIcon,
} from "lucide-react";

import { dataProvider } from "./dataProvider";
import { useModuleAvailability } from "./api/modules";
import { authProvider, devSurfacesEnabled } from "./authProvider";
import { i18nProvider } from "./i18n/i18nProvider";
import { ServiceAccountList } from "./pages/ServiceAccountList";
import { ServiceAccountDetail } from "./pages/ServiceAccountDetail";
import { WassenSoortList } from "./pages/WassenSoortList";
import { WassenSoortDetail } from "./pages/WassenSoortDetail";
import { ServiceAccountCreateWizard } from "./pages/ServiceAccountCreateWizard";
import { ServiceAccountSelfService } from "./pages/ServiceAccountSelfService";
import { ProductsOverview } from "./pages/ProductsOverview";
import { AdvertentiesOverview } from "./pages/AdvertentiesOverview";
import { AsielTariefOverview } from "./pages/AsielTariefOverview";
import { PensionTariefOverview } from "./pages/PensionTariefOverview";
import { PensionArrangementen } from "./pages/PensionArrangementen";
import { CrecheTariefOverview } from "./pages/CrecheTariefOverview";
import { DierenambulanceTariefOverview } from "./pages/DierenambulanceTariefOverview";
import { CrematoriumHandelingOverview } from "./pages/CrematoriumHandelingOverview";
import { CrematoriumVergoedingen } from "./pages/CrematoriumVergoedingen";
import { CrematoriumTariefOverview } from "./pages/CrematoriumTariefOverview";
import { CrematoriumVervoerOverview } from "./pages/CrematoriumVervoerOverview";
import { CrematoriumDashboard } from "./pages/CrematoriumDashboard";
import { CrematoriumPlanningList } from "./pages/CrematoriumPlanningList";
import { CrematoriumRunsList } from "./pages/CrematoriumRunsList";
import { CrematoriumAsRetourList } from "./pages/CrematoriumAsRetourList";
import { AanmeldingenList } from "./pages/AanmeldingenList";
import { CrematoriumUitvaartOverzichten } from "./pages/CrematoriumUitvaartOverzichten";
import { CrematieDossierDetail } from "./pages/CrematieDossierDetail";
import { CrematieAanmeldingWizard, type CrematieWizardKlant } from "./pages/CrematieAanmeldingWizard";
import { CrematoriumFacturenList } from "./pages/CrematoriumFacturenList";
import { EntingenOverzicht } from "./pages/EntingenOverzicht";
import { OntwormenOverzicht } from "./pages/OntwormenOverzicht";
import { MedicijnOverzicht } from "./pages/MedicijnOverzicht";
import { SchermbouwerPagina } from "./pages/schermbouwer/SchermbouwerPagina";
import { SchermbouwerBeheer } from "./pages/schermbouwer/SchermbouwerBeheer";
import { NazorgBeheer } from "./pages/NazorgBeheer";
import { NazorgDetail } from "./pages/NazorgDetail";
import { AnimalPresentView } from "./pages/AnimalPresentView";
import { AnimalPlacedView } from "./pages/AnimalPlacedView";
import { OpvangModuleBar, isOpvangModulePage } from "./pages/OpvangModules";
import { PensionLijstBalk, isPensionLijstPage, isPensionModulePage } from "./pages/PensionModules";
import type { Onderdeel as PensionOnderdeel } from "./pages/pensionoverzichten/velden";
import { OpvangOverzichten } from "./pages/OpvangOverzichten";
import { PleeggezinOverzicht } from "./pages/PleeggezinOverzicht";
import { AnimalDetail } from "./pages/AnimalDetail";
import { Kennelplanning } from "./pages/Kennelplanning";
import { RelatieDetail } from "./pages/RelatieDetail";
import { Kasboek } from "./pages/Kasboek";
import { Dagafsluiting } from "./pages/Dagafsluiting";
import { FactuurOverzicht } from "./pages/FactuurOverzicht";
import { RelatieCreateDialog } from "./pages/RelatieCreateDialog";
import { FactuurList } from "./pages/FactuurList";
import { FactuurDetail } from "./pages/FactuurDetail";
import { InboekenWizard } from "./pages/InboekenWizard";
import { ReleaseWizard } from "./pages/ReleaseWizard";
import { WizardHost } from "./pages/WizardHost";
import { Uitwisselingen } from "./pages/Uitwisselingen";
import { TagsHub } from "./pages/TagsHub";
import { SettingsDashboard } from "./pages/settings/SettingsDashboard";
import type { WizardBuilderInit } from "./pages/settings/WizardBuilderSettings";
import { UnderConstruction } from "./pages/UnderConstruction";
import { LoginPage } from "./pages/LoginPage";
import { SetPasswordPage } from "./pages/SetPasswordPage";
import { ForcePasswordChangePage } from "./pages/ForcePasswordChangePage";
import { getMustChangePassword, subscribeMustChangePassword } from "./auth/mustChangePassword";
import { neemVerseAanmelding } from "./auth/verseAanmelding";
import { Voorkeuren } from "./pages/Voorkeuren";
import { FOTO_GEWIJZIGD, profielfotoUrl } from "./api/profielfoto";
import { CommunicatieReadView } from "./pages/CommunicatieReadView";
import { Dashboard } from "./pages/Dashboard";
import { PensionDashboard } from "./pages/PensionDashboard";
import { PensionOverzichten } from "./pages/PensionOverzichten";
import { PensionPlanning } from "./pages/PensionPlanning";
import { PensionKlant } from "./pages/PensionKlant";
import { PensionBodycheck } from "./pages/PensionBodycheck";
import { PensionBorstelen } from "./pages/PensionBorstelen";
import { PensionMedicatie } from "./pages/PensionMedicatie";
import { PensionVoerlijst } from "./pages/PensionVoerlijst";
import { PensionWegen } from "./pages/PensionWegen";
import { Klantverzoeken } from "./pages/Klantverzoeken";
import { KlantverzoekDetail } from "./pages/KlantverzoekDetail";
import { KlantberichtDetail, Klantberichten } from "./pages/Klantberichten";
import { PortaalInstellingen } from "./pages/PortaalInstellingen";
import { UserList } from "./pages/UserList";
import { ModulebeheerScreen } from "./pages/ModulebeheerScreen";
import { VestigingenBeheer } from "./pages/vestigingen/VestigingenBeheer";
import { EntityAdminHub } from "./pages/entityAdmin/EntityAdminHub";
import { UserDetail } from "./pages/UserDetail";
import { UserCreate } from "./pages/UserCreate";
import { VestigingSwitcher } from "./components/VestigingSwitcher";
import { readWerkmodus, useWerkmodus, type Werkmodus } from "./components/werkmodus";
import { GlobalSearch } from "./components/GlobalSearch";
import { DataInsight } from "./pages/DataInsight";
import { SearchResults } from "./pages/SearchResults";
import { Rondleiding } from "./rondleiding/Rondleiding";
import { ImportTaakMelding } from "./pages/communicatie/editor/ImportTaakMelding";
import { importTaak } from "./pages/communicatie/editor/importTaak";
import { AiVoorstelMelding } from "./pages/settings/AiVoorstelMelding";
import { aiTaakVoorstel, aiWizardVoorstel } from "./pages/settings/achtergrondAi";
import { RONDLEIDINGEN } from "./rondleiding/rondleidingen";
import { zetBenaming, type Benaming } from "./benaming";

// Pages renderable in the SPA. The menu (NAV below) only links the *migrated* ones; the rest stay
// reachable via deep-link/action (self-service via the profile card; wassen-soorten + tags via URL)
// even though they have no left-menu entry — see initialPageFromPath / the MR deviations note.
type Page =
  // dashboard migration (Jmix-exit): the SPA /home landing dashboard. A DISTINCT deep-link path
  // (/dashboard-react) keeps the legacy Jmix `/home` route reachable during the strangler-fig migration.
  | "dashboard"
  // werkmodus (pension): the boarding administration gets its own landing dashboard — the asiel
  // Command Center is shelter-scoped (intakes, vaccinations, occupancy), so pension does not share it.
  | "pension-dashboard"
  | "pension-overzichten"
  | "pension-wegen"
  | "pension-bodycheck"
  // Klantportaal: de werklijst van verzoeken die klanten via het portaal indienen (ontwerp hoofdstuk 21).
  | "klantverzoeken"
  | "klantberichten"
  | "portaal-instellingen"
  | "pension-borstelen"
  | "pension-medicatie"
  | "pension-voerlijst"
  | "service-accounts"
  | "users"
  | "new-user"
  | "wassen-soorten"
  | "new-service-account"
  | "self-service"
  | "producten"
  | "asiel-tarief"
  | "pension-tarief"
  | "pension-arrangementen"
  | "creche-tarief"
  | "dierenambulance-tarief"
  | "crematorium-handelingen"
  | "crematorium-vergoedingen"
  | "crematorium-tarief"
  | "crematorium-vervoer"
  | "crematorium-dashboard"
  // crematorium-planning Epic 21 (MR-4): the read-only Planning / Runs / As-retour overviews.
  | "crematorium-planning"
  | "crematorium-runs"
  | "crematorium-asretour"
  | "aanmeldingen"
  | "crematies"
  // Crematorium Epic 21 MR-2: the read-only dossier detail (off-menu — opened from the Crematies list).
  | "crematie-dossier"
  // Crematorium Epic 21 MR-W4: the Aanmelding create-wizard (off-menu — opened from "Nieuwe aanmelding").
  | "crematie-aanmelding"
  | "crematorium-facturen"
  | "entingen-overzicht"
  | "ontwormen-overzicht"
  | "medicijn-overzicht"
  // nazorg-beheer (aftercare admin) migration: the SPA Nazorg screen. The Jmix `nazorg` view stays
  // reachable by its @Route until parity is signed off (strangler-fig).
  | "nazorg"
  | "animals"
  // sidebar-menu-parity: the tabbed "Opvangoverzichten" bundle (Jmix OpvangOverzichten.view) — the
  // single Application-group menu entry that hosts the Aanwezig/Entingen/Ontwormen/Medicijn/Vertrokken
  // overviews as tabs. The individual pages below stay as distinct Page states for deep-links/actions.
  | "opvang-overzichten"
  | "dieren-aanwezig"
  | "dieren-geplaatst"
  // pleeggezin-overzicht migration (Track B): the SPA foster-family overview (Jmix
  // `PleeggezinOverzichtView` / frontend-react/pleeggezin-overzicht hybrid).
  | "pleeggezin-overzicht"
  | "kennelplanning"
  // Data & Insight: eigen scherm om vragen te stellen over de eigen gegevens. Dit stond eerst
  // alleen achter de zoekbalk, en wie dat niet wist, wist niet dat het bestond.
  | "data-insight"
  // AI-schermbouwer: zelf een tegel laten ontwerpen in gewone taal, binnen de catalogus van een
  // tegeltype. Geen moduleKey -- wat je mag zien en maken bepaalt de catalogus per gebruiker.
  | "schermbouwer"
  // Beheer van de schermbouwer: wat de AI mag gebruiken, wat wacht op goedkeuring, en wat mensen
  // missen. Hoort bij de beheerder, niet bij de ontwerper -- vandaar een eigen scherm in Security.
  | "schermbouwer-beheer"
  | "relaties"
  // invoices-migration Epic 6: the unified, standalone invoice list/browse — a left-menu entry point.
  | "financieel-overzichten"
  // Financieel → Kasboek: eigen scherm (geen tab van het facturenoverzicht) — hier wordt geboekt.
  | "kasboek"
  | "losse-verkoop"
  // Financieel → Dagafsluiting: de kas in vier stappen afsluiten.
  | "dagafsluiting"
  | "facturen"
  // invoices-migration Epic 1: the read-only React invoice-detail screen (reached from the Relaties
  // Facturen tile / a search invoice hit / the Epic 6 list). Off-menu — opened via a factuur id.
  | "factuur"
  | "inboeken"
  | "uitboeken"
  | "uitwisselingen"
  | "tags-hub"
  | "instellingen"
  | "modulebeheer"
  // entity-admin migration (Jmix-exit): the generic docasoft-CRUD framework replacing the ~189 generated
  // `view/docasoft/**` List+Detail pairs. Off-menu / URL-reachable (`/entity-admin[/{key}]`), like the
  // other un-menued screens — the generated screens were never in the curated sidebar.
  | "entity-admin"
  // vestigingen-beheer migration: the SPA branch-admin screen (Security menu). Edit-only — replaces the
  // Jmix `Vestigingen.beheer` / @Route "vestigingen-beheer" iframe. Distinct from the header switcher.
  | "vestigingen"
  // communicatie (read-foundation UI): the SPA read screens (templates/log/media). Wired ADDITIVELY
  // alongside the legacy Jmix `communicatie` / `communicatie-log` menu handoffs (strangler-fig).
  | "communicatie-react"
  // advertentie-migration (Epic 22/23 Jmix-exit): the React Advertentie list + read-only detail. The
  // Jmix `Advertentie.list` view stays reachable by its @Route until the editor/builder/publish flows
  // also migrate (strangler-fig).
  | "advertenties"
  // search-migration E3: the global-search results route (off-menu — reached from the header bar).
  | "zoekresultaten"
  // spa-session: a not-yet-migrated / unknown route handled in-SPA shows a placeholder (keeps the
  // operator logged in instead of a full-page navigation that drops the in-memory token).
  | "voorkeuren"
  | "under-construction";

// Per-page header title key (i18n). Pages reachable only via an action (the create wizard) still need
// a title here.
const PAGE_TITLE_KEY: Record<Page, string> = {
  dashboard: "menu.dashboard",
  "pension-dashboard": "menu.dashboard",
  "pension-overzichten": "menu.pensionOverzichten",
  "pension-wegen": "menu.pensionWegen",
  "pension-bodycheck": "menu.pensionBodycheck",
  klantverzoeken: "menu.klantverzoeken",
  klantberichten: "menu.klantberichten",
  "portaal-instellingen": "menu.portaalInstellingen",
  "pension-borstelen": "menu.pensionBorstelen",
  "pension-medicatie": "menu.pensionMedicatie",
  "pension-voerlijst": "menu.pensionVoerlijst",
  "service-accounts": "menu.serviceAccounts",
  users: "menu.users",
  "new-user": "userCreateView.title",
  "new-service-account": "serviceAccountCreateView.title",
  "self-service": "serviceAccountSelfServiceView.title",
  producten: "menu.productenCatalogus",
  "asiel-tarief": "menu.asielTarief",
  "pension-tarief": "menu.pensionTarief",
  "pension-arrangementen": "menu.pensionArrangementen",
  "creche-tarief": "menu.crecheTarief",
  "dierenambulance-tarief": "menu.dierenambulanceTarief",
  "crematorium-handelingen": "menu.crematoriumHandeling",
  "crematorium-vergoedingen": "menu.crematoriumVergoedingen",
  "crematorium-tarief": "menu.crematoriumTarief",
  "crematorium-vervoer": "menu.crematoriumVervoer",
  "crematorium-dashboard": "menu.crematoriumDashboard",
  "crematorium-planning": "menu.crematoriumPlanning",
  "crematorium-runs": "menu.crematoriumRuns",
  "crematorium-asretour": "menu.crematoriumAsRetour",
  aanmeldingen: "menu.crematoriumAanmeldingen",
  crematies: "menu.crematoriumUitvaartoverzichten",
  "crematie-dossier": "crematieDetail.heading",
  "crematie-aanmelding": "crematieAanmelding.heading",
  "crematorium-facturen": "menu.crematoriumFacturen",
  "entingen-overzicht": "menu.entingenOverzicht",
  "ontwormen-overzicht": "menu.ontwormenOverzicht",
  "medicijn-overzicht": "menu.medicijnOverzicht",
  nazorg: "nazorg.title",
  "wassen-soorten": "wassenSoortListView.title",
  animals: "menu.animal",
  "opvang-overzichten": "menu.opvangOverzichten",
  "dieren-aanwezig": "menu.dierAttendance",
  "dieren-geplaatst": "menu.dierPlacement",
  "pleeggezin-overzicht": "pgOverzicht.title",
  kennelplanning: "kennelplanning.title",
  "data-insight": "dataInsight.title",
  schermbouwer: "schermbouwer.title",
  "schermbouwer-beheer": "schermbouwer.beheerTitle",
  relaties: "menu.relaties",
  "financieel-overzichten": "financieel.title",
  kasboek: "kasboek.title",
  "losse-verkoop": "losseVerkoop.title",
  dagafsluiting: "dagafsluiting.title",
  facturen: "factuurListView.title",
  factuur: "factuurDetailView.heading",
  inboeken: "inboeken.title",
  uitboeken: "uitboeken.title",
  uitwisselingen: "uitwisselingen.title",
  "tags-hub": "tagsHub.title",
  instellingen: "instellingen.title",
  modulebeheer: "moduleManagementView.title",
  vestigingen: "vestigingenBeheer.title",
  "entity-admin": "entityAdmin.catalogTitle",
  "communicatie-react": "communicatieRead.title",
  advertenties: "menu.advertentieBeheer",
  zoekresultaten: "search.results.title",
  voorkeuren: "voorkeuren.title",
  "under-construction": "underConstruction.title",
};

// Detail/form pages that on acc are still the 80% Vaadin shell (not yet migrated to a React module).
// The SPA renders them at the denser 80% to match acc (per-screen density, see AppShell.contentDense).
// Migrated React-module pages — the overviews, product detail, vestigingen, the wizards — are 100% on
// acc (React iframes) and are deliberately NOT in this set.
const DENSE_PAGES = new Set<Page>([
  "animals",
  "relaties",
]);

// ---- Sidebar navigation: an exact mirror of the LIVE Jmix sidebar ---------------------------------
// GROUND TRUTH: nl.docasoft.fawkes.backoffice.service.sidebar.SidebarModelBuilder + the
// frontend-react/sidebar hybrid app it feeds — that is what a Jmix user actually SEES. It supersedes
// both menu.xml (which e.g. lists a Wizards group the shipped sidebar deliberately omits) and the old
// main-view.css theme rules. Groups, items, order, Dutch labels (via i18n) and the per-item/per-group
// Lucide icons (`glyph` = the SidebarModelBuilder icon name, rendered via lucide-react exactly like
// frontend-react/sidebar/src/Icon.jsx) reproduce that model. Each group carries its header treatment
// via `mode`:
//   • "static"    — a static uppercase label, always open (the Jmix "Applicatie" group);
//   • "open"      — a collapsible group, expanded by default. No group uses it today: the menu is
//                   long enough that everything below APPLICATIE opens collapsed (maintainer), so the
//                   day-to-day screens stay reachable without scrolling past four open accordions.
//   • "collapsed" — a collapsible group with a section glyph, collapsed by default; `menu.xml`'s
//                   `opened="false"`.
//
//   • kind: "spa"    — a migrated screen, rendered in-SPA (state nav).
//   • kind: "legacy" — not-yet-migrated; hands off to the classic Jmix app at the site root. During
//                      coexistence the SPA is fronted by Caddy under /new/** while Jmix serves the
//                      legacy views at the root, so a root-absolute href (`/home`, `/animal`, …)
//                      lands on Jmix. `route` is the Jmix view's @Route value.
//
// Deliberate deviations from menu.xml (called out in the MR):
//   • Service Accounts — removed from the Jmix menu when it was cut over to the SPA; kept here in its
//     historical Security group as an in-SPA item so it is not orphaned.
//   • Wassoorten / Tags / "Mijn account" — no Jmix main-menu entry (tags/wash-types live under the
//     Jmix Settings dashboard; self-service is a profile action). Off-menu but still reachable.
// Module gate (Modulebeheer / Jmix parity): `moduleKey` ties a menu item to its owning module, exactly
// mirroring the Jmix ModuleRegistry's menu-item/route ownership (backoffice config/module/ModuleRegistry
// .java ↔ the api registry serving GET /api/modules). An item with a moduleKey is hidden when
// `isModuleEnabled(key)` says the module is off; a group whose items are all hidden disappears with
// them. No moduleKey = always visible (kern screens: dashboard, dieren, relaties, facturen, security).
// The hook is permissive while the feed loads/errs, so the menu never flickers away on a blip.
type GroupMode = "static" | "open" | "collapsed";
type SpaItem = { kind: "spa"; page: Page; labelKey: string; glyph?: string; moduleKey?: string };
type LegacyItem = { kind: "legacy"; route: string; labelKey: string; glyph?: string; moduleKey?: string };
type NavItemDef = SpaItem | LegacyItem;
type NavGroup = { titleKey: string; mode: GroupMode; sectionGlyph?: string; items: NavItemDef[] };

export const NAV: NavGroup[] = [
  {
    titleKey: "menu.application.title",
    // The live Jmix APPLICATIE section is a static uppercase heading (SidebarModelBuilder:
    // collapsible=false), not an accordion.
    mode: "static",
    items: [
      // Migrated (dashboard migration, Jmix-exit): the SPA /home landing dashboard (Dashboard). The
      // Jmix `home` (FawkesHome) view is NOT removed yet (strangler-fig) — it stays reachable by its
      // @Route until the React replacement reaches full parity. The SPA uses a DISTINCT deep-link path
      // (/dashboard-react) so it never collides with the legacy `/home` route.
      { kind: "spa", page: "dashboard", labelKey: "menu.dashboard", glyph: "layout-grid" },
      // Migrated (animal-detail, Jmix-exit): the SPA AnimalList → AnimalDetail. The Jmix `animal`
      // view is NOT removed yet (strangler-fig) — it stays reachable by its @Route until the React
      // replacement reaches full parity (the editable tiles land in later phases).
      { kind: "spa", page: "animals", labelKey: "menu.animal", glyph: "dog" },
      // Migrated (relaties-detail, Jmix-exit): the SPA RelatieList → RelatieDetail. Closes story 9.14
      // (the Jmix `relaties` route opened a blank create-form, not a list). The Jmix view is NOT
      // removed yet (strangler-fig) — it stays reachable by its @Route until the React replacement
      // reaches full parity (the remaining tiles + role tabs + writes land in later phases).
      { kind: "spa", page: "relaties", labelKey: "menu.relaties", glyph: "users" },
      // sidebar-menu-parity: the tabbed "Opvangoverzichten" bundle (Jmix OpvangOverzichten.view). The
      // Jmix redesign consolidated the loose overviews (Aanwezig/Entingen/Ontwormen/Medicijn/Vertrokken)
      // under this single menu item — mirrored here. Ungated, matching SidebarModelBuilder (no menuId).
      // Pleeggezin overzichten, Nazorg and Uitwisselingen used to sit here as their own entries; they
      // are now segments of this one screen (maintainer 2026-08-18: "ik wil de items in het linker menu
      // verplaatst hebben naar opvangoverzichten"). Their pages, URLs and module gates are unchanged —
      // see OpvangModules.tsx and PAGE_MODULE_KEY below.
      { kind: "spa", page: "opvang-overzichten", labelKey: "menu.opvangOverzichten", glyph: "clipboard-list" },
      // Kennelplanning: only the migrated React screen is exposed (labelled plain "Planning"); the
      // legacy Jmix hand-off entry was dropped now the React version stands in for it.
      { kind: "spa", page: "kennelplanning", labelKey: "menu.kennelplanning", glyph: "calendar-days", moduleKey: "kennelplanning" },
      // Data & Insight: geen moduleKey — het scherm werkt op de gegevens die er toch al zijn, en
      // een module om aan of uit te zetten bestaat ervoor niet. Wie geen ROLE_SEARCH_AI heeft
      // krijgt van de api een 403 en het scherm zegt dat; verbergen kan hier niet, want de schil
      // kent de rechten van de gebruiker niet.
      { kind: "spa", page: "data-insight", labelKey: "menu.dataInsight", glyph: "bar-chart" },
      // Losse verkoop: wat er over de toonbank gaat zonder klant. Staat hier en niet bij Financieel,
      // want het is balie-werk -- de factuur die eruit komt vind je daarna gewoon bij de facturen.
      { kind: "spa", page: "losse-verkoop", labelKey: "menu.losseVerkoop", glyph: "shopping-cart" },
      // AI-schermbouwer: naast Data & Insight, want het is dezelfde soort vraag -- iets uit je eigen
      // gegevens krijgen zonder dat iemand er code voor hoeft te schrijven. Staat uit het menu
      // (Maarten, 2026-10-20): nog niet klaar om aan klanten te laten zien. Het scherm zelf blijft
      // bestaan en blijft bereikbaar op zijn eigen adres -- alleen de ingang is weg, zodat niemand er
      // per ongeluk in loopt. Deze regel terugzetten is het enige wat er hoeft te gebeuren.
    ],
  },
  {
    titleKey: "menu.producten.title",
    mode: "collapsed",
    sectionGlyph: "package",
    items: [
      // Migrated: the SPA ProductList.
      { kind: "spa", page: "producten", labelKey: "menu.productenCatalogus", glyph: "boxes" },
      // Migrated (verblijf-wizard-tarief Epic 3): the SPA PensionTariefOverview (Tarieven /
      // Hoogseizoen / Kortingen). The Jmix `pension-tarief` view stays reachable by its @Route until
      // the React replacement reaches full parity (strangler-fig).
      { kind: "spa", page: "pension-tarief", labelKey: "menu.pensionTarief", glyph: "bed-double", moduleKey: "pension" },
      // Arrangementen en extra's die de balie in de wizard Nieuwe reservering kiest; het pension richt ze hier zelf in.
      { kind: "spa", page: "pension-arrangementen", labelKey: "menu.pensionArrangementen", glyph: "gift", moduleKey: "pension" },
      // Migrated (tarief-types Epic 4): the SPA CrecheTariefOverview (Losse dagen / Rittenkaarten /
      // Abonnementen). The Jmix `creche-tarief` view stays reachable by its @Route until the React
      // replacement reaches full parity (strangler-fig).
      { kind: "spa", page: "creche-tarief", labelKey: "menu.crecheTarief", glyph: "baby", moduleKey: "creche" },
      { kind: "spa", page: "asiel-tarief", labelKey: "menu.asielTarief", glyph: "house", moduleKey: "asiel" },
      // Migrated (tarief-types Epic 5): the SPA DierenambulanceTariefOverview (single rates grid with
      // Prijs wijzigen / Historie tonen; "Tarief Toevoegen" + "Historie-overzicht" reproduced as disabled
      // stubs, faithful to the Jmix view). The Jmix `dierenambulance-tarief` view stays reachable by its
      // @Route until the React replacement reaches full parity (strangler-fig).
      // Ambulance is asiel (Jmix registry: dierenambulanceTarief is an asiel-owned menu id).
      { kind: "spa", page: "dierenambulance-tarief", labelKey: "menu.dierenambulanceTarief", glyph: "ambulance", moduleKey: "asiel" },
      // Migrated (Crematorium Epic 21, MR-0): the SPA CrematoriumTariefOverview (Gewichtsklassen +
      // Tarieven) and CrematoriumVervoerOverview. The Jmix `crematorium-tarief` / `crematorium-vervoer`
      // views stay reachable by their @Route until parity is signed off (strangler-fig).
      { kind: "spa", page: "crematorium-tarief", labelKey: "menu.crematoriumTarief", glyph: "flame", moduleKey: "crematorium" },
      { kind: "spa", page: "crematorium-vervoer", labelKey: "menu.crematoriumVervoer", glyph: "truck", moduleKey: "crematorium" },
      // De stappen van een uitvaart zijn een instelling per vestiging (uitvaartvormen + handelingen);
      // het dossier en de planning leiden hun termijnen hieruit af.
      { kind: "spa", page: "crematorium-handelingen", labelKey: "menu.crematoriumHandeling", glyph: "list-checks", moduleKey: "crematorium" },
    ],
  },
  // NOTE — no Wizards group: menu.xml lists one (asielWizard/nazorgWizard) but the LIVE sidebar
  // (SidebarModelBuilder) deliberately omits it, so a Jmix user never sees it. The wizard-beheer /
  // nazorg-wizard routes stay reachable via the Nazorg screen's hand-offs and by direct URL.
  {
    // sidebar-menu-parity: mirrors the live `crematorium` group (5 items). The SPA-only Runs / As-retour
    // entries were dropped — in Jmix they are sub-views of Planning, not menu items; the read-only React
    // screens stay reachable off-menu (from the dashboard / planning). "Instellingen" likewise has no
    // Jmix menu entry.
    titleKey: "menu.crematorium.title",
    mode: "collapsed",
    sectionGlyph: "flame",
    items: [
      // Migrated (Crematorium Epic 21, MR-5): the SPA dashboard. The Jmix dashboard view stays
      // reachable by its @Route (strangler-fig).
      { kind: "spa", page: "crematorium-dashboard", labelKey: "menu.crematoriumDashboard", glyph: "layout-grid", moduleKey: "crematorium" },
      // Migrated (Crematorium Epic 21, MR-3): the SPA Aanmeldingen worklist (read). The create-wizard
      // is a deferred write follow-up, so "Nieuwe aanmelding" still opens the Jmix wizard; the Jmix
      // list view stays reachable by its @Route (strangler-fig).
      { kind: "spa", page: "aanmeldingen", labelKey: "menu.crematoriumAanmeldingen", glyph: "inbox", moduleKey: "crematorium" },
      // Migrated (Crematorium Epic 21, MR-2): the SPA Crematies dossier list + read-only detail. The
      // Jmix `crematorium-crematies` dispatcher/detail views stay reachable by @Route (strangler-fig).
      // Live label: "Dossiers" (SidebarModelBuilder), not the menu.xml "Crematies".
      // Uitvaartoverzichten bundelt dossiers, ovenruns en as-retour; die laatste twee stonden niet
      // in het menu en waren daardoor alleen via een directe link te vinden.
      { kind: "spa", page: "crematies", labelKey: "menu.crematoriumUitvaartoverzichten", glyph: "folder-open", moduleKey: "crematorium" },
      // Migrated (Crematorium Epic 21, MR-4): the read-only SPA Planning overview (Runs / As-retour are
      // reached from within it, matching the Jmix Planning view's sub-tabs).
      { kind: "spa", page: "crematorium-planning", labelKey: "menu.crematoriumPlanning", glyph: "calendar", moduleKey: "crematorium" },
      // Migrated (Crematorium Epic 21, MR-1): the SPA CrematoriumFacturenList. The Jmix
      // `crematorium-facturen` view stays reachable by its @Route (strangler-fig).
      { kind: "spa", page: "crematorium-facturen", labelKey: "menu.crematoriumFacturen", glyph: "receipt", moduleKey: "crematorium" },
    ],
  },
  {
    // FINANCIEEL — the money screens as their own group (the acc sidebar has one). Only Facturen is
    // migrated; it had no sidebar entry at all until now (you reached the invoice list through search
    // or a relation tile). Overzichten / Betalingen / Kasboek / Dagafsluiting / Export belong here too
    // but have neither an SPA screen nor a Jmix route in this codebase yet — they are added as they
    // land, rather than as menu items that lead nowhere.
    titleKey: "menu.financieel.title",
    mode: "collapsed",
    sectionGlyph: "wallet",
    items: [
      // Facturen en Betalingen zijn tabs van één scherm (met Omzet erbij), dus ze staan hier als één
      // ingang: "Overzichten". Twee menu-items naar hetzelfde scherm laten de gebruiker kiezen tussen
      // dezelfde twee dingen die in het scherm zelf al naast elkaar staan.
      { kind: "spa", page: "financieel-overzichten", labelKey: "menu.financieelOverzichten", glyph: "receipt" },
      { kind: "spa", page: "kasboek", labelKey: "menu.kasboek", glyph: "book-open" },
      { kind: "spa", page: "dagafsluiting", labelKey: "menu.dagafsluiting", glyph: "calendar-check" },
      { kind: "spa", page: "under-construction", labelKey: "menu.financieelExport", glyph: "download" },
    ],
  },
  {
    titleKey: "menu.communicatie.title",
    mode: "collapsed",
    sectionGlyph: "messages-square",
    items: [
      // Communicatie: only the migrated React screen is exposed (labelled plain "Communicatie"); it
      // covers templates/log/media, so the two live Jmix entries (Communicatiebeheer settings-2 +
      // Communicatielog message-square-text) consolidate into one — a user-confirmed deviation. The
      // item borrows the beheer icon. System module: not user-toggleable in Modulebeheer, but config
      // can still disable it — the Jmix registry gates the `communicatie` menu id the same way.
      { kind: "spa", page: "communicatie-react", labelKey: "menu.communicatie", glyph: "settings-2", moduleKey: "communicatie" },
    ],
  },
  {
    titleKey: "menu.security.title",
    mode: "collapsed",
    sectionGlyph: "shield-check",
    items: [
      // Migrated (admin-users): the Jmix `users` screen → the SPA UserList/UserDetail. The Jmix view
      // is kept reachable by its @Route (role-assignment still lives there) until that migrates too.
      // Label: the live sidebar says "Users" (an English slip in SidebarModelBuilder's hardcoded
      // labels); the SPA keeps proper Dutch "Gebruikers" — user-confirmed deviation.
      { kind: "spa", page: "users", labelKey: "menu.users", glyph: "user-round" },
      // Migrated (vestigingen-beheer, Jmix-exit): the Jmix `vestigingen` (Vestigingen.beheer / @Route
      // "vestigingen-beheer") admin screen → the SPA VestigingenBeheer (edit-only). Distinct from the
      // header VestigingSwitcher, which only changes the active branch. Placed after users, matching the
      // live order. The Jmix view stays reachable by its @Route (strangler-fig).
      { kind: "spa", page: "vestigingen", labelKey: "menu.vestigingen", glyph: "building-2" },
      // Deviation: Service Accounts was cut over to the SPA (jve-5-3b-4) and removed from the Jmix
      // sidebar. Kept here in its historical Security group as an in-SPA screen so it isn't orphaned.
      // key-round = the icon the sidebar design reserved for it (frontend-react/sidebar Icon.jsx map).
      { kind: "spa", page: "service-accounts", labelKey: "menu.serviceAccounts", glyph: "key-round" },
      { kind: "spa", page: "modulebeheer", labelKey: "menu.moduleManagement", glyph: "blocks" },
      // Het beheerdeel van de schermbouwer. De bouwer zelf staat bij APPLICATIE; dit is de kant die
      // over andermans schermen gaat en die achter ROLE_SETTINGS_ADMIN zit.
      { kind: "spa", page: "schermbouwer-beheer", labelKey: "menu.schermbouwerBeheer", glyph: "sparkles" },
    ],
  },
  // The "Data Tools" group (feature-flag-gated Jmix "Entity inspector" / backoffice-test-view) was
  // removed: production is SPA-only, so the legacy link was dead. The Jmix view itself stays as a
  // local dev tool (won't migrate — see jmix-migration-backlog.md row 7).
];

// Werkmodus (the Asiel/Pension segments in the vestiging card): switching modes rewrites ONLY the
// APPLICATIE section — every group below it (Producten, Financieel, Communicatie, Security …) is
// shared administration and stays exactly as it is. In pension mode:
//   • Dashboard          → its own boarding landing page (the asiel Command Center is shelter-scoped:
//                          intakes, vaccinations, shelter occupancy);
//   • Relatie            → Dier + Relaties merged into ONE entry: a boarding animal is always reached
//                          through the customer it belongs to, so there is no standalone animal list;
//   • Pensionoverzichten → the same overviews bundle under its boarding name;
//   • Planning           → the same entry and the same screen (its boarding variant shows once opened).
const PENSION_APPLICATIE: NavGroup = {
  titleKey: "menu.application.title",
  mode: "static",
  items: [
    { kind: "spa", page: "pension-dashboard", labelKey: "menu.dashboard", glyph: "layout-grid" },
    { kind: "spa", page: "relaties", labelKey: "menu.pensionRelatie", glyph: "users" },
    { kind: "spa", page: "pension-overzichten", labelKey: "menu.pensionOverzichten", glyph: "clipboard-list", moduleKey: "pension" },
    // Hangt aan pension en niet aan kennelplanning: die module valt onder asiel ("asiel uit → ook deze
    // weg"), en een pension zonder asiel (Dierenhotel Marijke, oktober 2026) verloor zo zijn planning.
    // In deze modus is het scherm de pensionplanning, die niets van de asielplanning gebruikt.
    { kind: "spa", page: "kennelplanning", labelKey: "menu.kennelplanning", glyph: "calendar-days", moduleKey: "pension" },
    // Wegen (wens §2), Bodycheck (§4), Borstelen (§8), Medicatie (§9) en Voerlijst (§5) staan sinds
    // 2026-10-05 als onderdelen in de balk boven Pensionoverzichten (PensionModules), niet meer in het menu.
    // Klantportaal: wat klanten aanvragen, wacht hier op een medewerker.
    { kind: "spa", page: "klantverzoeken", labelKey: "menu.klantverzoeken", glyph: "clipboard-list" },
    { kind: "spa", page: "klantberichten", labelKey: "menu.klantberichten", glyph: "messages-square" },
    // Portaalinstellingen staat niet meer in het menu maar als knop op Klantverzoeken: je gaat erheen
    // vanuit het werk ("hier klopt iets niet aan de instellingen"), niet als losse bestemming. De
    // pagina en de route /portaal-instellingen blijven bestaan, dus een bladwijzer werkt gewoon door.
  ],
};

// In crematoriummodus is de dagelijkse vraag een andere: niet welke dieren er zijn, maar welke
// uitvaarten lopen en wat er vandaag moet gebeuren. Vandaar vier ingangen:
//   • Dashboard          → het crematoriumdashboard;
//   • Klanten            → hetzelfde relatiescherm, maar zo heet een relatie hier: de nabestaande.
//                          De tegel Crematies op dat scherm is de ingang naar de dossiers;
//   • Uitvaartoverzichten → dossiers, ovenruns en as-retour bij elkaar;
//   • Planning           → de werklijst: wat moet er wanneer gebeuren.
// In deze modus vervalt de losse CREMATORIUM-groep: zijn ingangen staan nu bovenin, en hem laten
// staan zou hetzelfde tweemaal in het menu zetten. Buiten deze modus blijft de groep gewoon staan,
// dus Aanmeldingen en Facturen zijn daar nog te vinden (en overal via hun eigen url).
const CREMATORIUM_APPLICATIE: NavGroup = {
  titleKey: "menu.application.title",
  mode: "static",
  items: [
    { kind: "spa", page: "crematorium-dashboard", labelKey: "menu.dashboard", glyph: "layout-grid" },
    { kind: "spa", page: "relaties", labelKey: "menu.crematoriumKlanten", glyph: "users" },
    { kind: "spa", page: "crematies", labelKey: "menu.crematoriumUitvaartoverzichten", glyph: "flame" },
    { kind: "spa", page: "crematorium-planning", labelKey: "menu.crematoriumPlanning", glyph: "calendar-days" },
  ],
};

// In crematoriummodus is de PRODUCTEN-groep die van de mockup: de catalogus (urnen, sieraden en
// aandenkens komen daarvandaan de factuur op), de crematietarieven en de ophaalkosten. De tarieven
// van asiel, pension en creche horen bij een andere administratie en blijven daar. Handelingen en
// Vergoedingen uit de mockup komt erbij zodra dat scherm er is — geen ingang naar niets.
const CREMATORIUM_PRODUCTEN: NavGroup = {
  titleKey: "menu.producten.title",
  mode: "collapsed",
  sectionGlyph: "package",
  items: [
    { kind: "spa", page: "producten", labelKey: "menu.productenCatalogus", glyph: "boxes" },
    { kind: "spa", page: "crematorium-tarief", labelKey: "menu.crematieTarieven", glyph: "flame", moduleKey: "crematorium" },
    { kind: "spa", page: "crematorium-vervoer", labelKey: "menu.ophaalkosten", glyph: "truck", moduleKey: "crematorium" },
    // Welke stappen een uitvaart doorloopt — in de mockup een instelling naast de tarieven.
    { kind: "spa", page: "crematorium-handelingen", labelKey: "menu.crematoriumHandeling", glyph: "list-checks", moduleKey: "crematorium" },
    { kind: "spa", page: "crematorium-vergoedingen", labelKey: "menu.crematoriumVergoedingen", glyph: "hand-coins", moduleKey: "crematorium" },
  ],
};

/**
 * Het menu van een werkmodus: de gedeelde groepen, met APPLICATIE vervangen door die van de modus.
 * Crematorium vervangt ook PRODUCTEN en laat de losse CREMATORIUM-groep vallen: haar ingangen staan
 * nu bovenin, en haar laten staan zou hetzelfde tweemaal in het menu zetten.
 */
export function navForModus(modus: Werkmodus): NavGroup[] {
  const eigen = modus === "pension" ? PENSION_APPLICATIE : modus === "crematorium" ? CREMATORIUM_APPLICATIE : null;
  const groepen = eigen ? NAV.map((group) => (group === NAV[0] ? eigen : group)) : NAV;

  // De crematoriumgroep staat in géén enkele modus als losse groep in de lijst. In de
  // crematoriummodus niet, want daar zijn zijn schermen de hoofdgroep geworden; in asiel en pension
  // niet, want daar werk je er niet in. Wie in het asiel staat heeft een lijst nodig die over het
  // asiel gaat: een groep die je nooit opent is ruis, en op een telefoon kost hij ook nog plek.
  // Het crematorium blijft één tik weg via de vestigingskaart bovenaan, die de modus omzet.
  const zonderCrematorium = groepen.filter((group) => group.titleKey !== "menu.crematorium.title");
  if (modus !== "crematorium") return zonderCrematorium;
  return zonderCrematorium.map((group) =>
    group.titleKey === "menu.producten.title" ? CREMATORIUM_PRODUCTEN : group);
}

// The NAV with disabled modules' items filtered out (and groups that end up empty dropped with them),
// mirroring the Jmix module system hiding its menu ids. Pure so App.nav.test can pin the behavior.
export function visibleNav(
  isModuleEnabled: (key: string) => boolean,
  modus: Werkmodus = "asiel",
): NavGroup[] {
  return navForModus(modus).map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.moduleKey || isModuleEnabled(item.moduleKey)),
  })).filter((group) => group.items.length > 0);
}

// Deep-link module guard: hiding a disabled module's menu items is not enough — a bookmarked/typed
// URL (or stale in-app state) still reaches the page. This maps every module-owned SPA page to its
// Jmix ModuleRegistry key so AuthedApp can render the module-disabled empty state instead. The map is
// DERIVED from the NAV `moduleKey` annotations (single source — menu gate and page gate can't drift),
// plus the off-menu detail/wizard sub-pages that have no menu entry: the Jmix registry owns their
// route segments too (`crematorium-dossier` / `crematorium-aanmelding` → crematorium).
export const PAGE_MODULE_KEY: Partial<Record<Page, string>> = {
  "crematie-dossier": "crematorium",
  "crematie-aanmelding": "crematorium",
  // sidebar-menu-parity: these pages left the sidebar (folded into the Opvangoverzichten bundle, or
  // Planning sub-views) but stay reachable by deep-link/action. Their Jmix ModuleRegistry route segments
  // remain module-owned, so keep the page-level guard even though they no longer carry a NAV moduleKey.
  "dieren-aanwezig": "asiel",
  "dieren-geplaatst": "asiel",
  // Same story for the three overviews that moved into the Opvangoverzichten switcher: no NAV entry
  // to derive the key from any more, so the deep-link guard keeps it explicitly.
  "pleeggezin-overzicht": "pleeggezin",
  nazorg: "nazorg",
  uitwisselingen: "shelter-exchanges",
  advertenties: "advertentie",
  "crematorium-runs": "crematorium",
  "crematorium-asretour": "crematorium",
  // Boarding landing page: pension-only, so the pension module guards it even though it stays
  // reachable by deep link from either werkmodus.
  "pension-dashboard": "pension",
  "pension-overzichten": "pension",
  "pension-wegen": "pension",
  "pension-bodycheck": "pension",
  klantverzoeken: "pension",
  klantberichten: "pension",
  "portaal-instellingen": "pension",
  "pension-borstelen": "pension",
  "pension-medicatie": "pension",
  "pension-voerlijst": "pension",
  // PENSION_APPLICATIE staat hier niet bij: zijn enige ingang zonder eigen regel hierboven is Planning,
  // en die deelt de pagina met de asielplanning. Afgeleid zou "pension" die van asiel overschrijven;
  // per werkmodus regelt isPageModuleEnabled het.
  ...Object.fromEntries(
    [...NAV, CREMATORIUM_APPLICATIE].flatMap((group) => group.items).flatMap((item) =>
      item.kind === "spa" && item.moduleKey ? [[item.page, item.moduleKey] as const] : [],
    ),
  ),
};

// Whether a page may render given module availability. Same permissive semantics as the menu gate:
// `isModuleEnabled` (useModuleAvailability) returns true while the feed is loading/unavailable, so a
// slow or failed module feed never blanks a page — only an explicit "disabled" blocks it. Pure so
// App.nav.test can pin the behavior.
export function isPageModuleEnabled(
  page: Page,
  isModuleEnabled: (key: string) => boolean,
  werkmodus?: Werkmodus,
): boolean {
  // In pensionmodus is Planning de pensionplanning: die hoort bij pension, niet bij de asielmodule.
  const moduleKey = page === "kennelplanning" && werkmodus === "pension" ? "pension" : PAGE_MODULE_KEY[page];
  return !moduleKey || isModuleEnabled(moduleKey);
}

// Sidebar icons — the exact Lucide set the live Jmix sidebar uses. Mirrors the kebab-case name →
// component map of frontend-react/sidebar/src/Icon.jsx (lucide-react, same ^0.460 dependency), so the
// `glyph` names in NAV read 1:1 against SidebarModelBuilder's icon names.
const NAV_ICONS: Record<string, LucideIcon> = {
  "layout-grid": LayoutGrid,
  scale: Scale,
  dog: Dog,
  users: UsersIcon,
  "clipboard-list": ClipboardList,
  "list-checks": ListChecks,
  "clipboard-check": ClipboardCheck,
  brush: Brush,
  pill: Pill,
  utensils: Utensils,
  "hand-heart": HandHeart,
  "heart-pulse": HeartPulse,
  "calendar-days": CalendarDays,
  "arrow-right-left": ArrowRightLeft,
  package: Package,
  boxes: Boxes,
  "bed-double": BedDouble,
  gift: Gift,
  baby: Baby,
  house: House,
  ambulance: Ambulance,
  flame: Flame,
  truck: Truck,
  inbox: Inbox,
  "folder-open": FolderOpen,
  calendar: Calendar,
  receipt: Receipt,
  "shopping-cart": ShoppingCart,
  "messages-square": MessagesSquare,
  "settings-2": Settings2,
  megaphone: Megaphone,
  "badge-percent": BadgePercent,
  "shield-check": ShieldCheck,
  "user-round": UserRound,
  "building-2": Building2,
  "key-round": KeyRound,
  blocks: Blocks,
  wrench: Wrench,
  "scan-search": ScanSearch,
  wallet: Wallet,
  "bar-chart": BarChart3,
  "credit-card": CreditCard,
  "book-open": BookOpen,
  "calendar-check": CalendarCheck,
  download: Download,
};

// Renders a NAV glyph like the live sidebar's Icon.jsx: items in the static APPLICATIE section at
// 19px, items inside collapsible groups at 16px, section-header icons at 18px in the muted green ink
// (frontend-react/sidebar Sidebar.jsx sizes/colour).
function glyphNode(glyph: string | undefined, size: number): ReactNode {
  const Cmp = glyph ? NAV_ICONS[glyph] : undefined;
  if (!Cmp) return undefined;
  return <Cmp size={size} strokeWidth={2} aria-hidden="true" />;
}

function sectionGlyphNode(glyph: string | undefined): ReactNode {
  const Cmp = glyph ? NAV_ICONS[glyph] : undefined;
  if (!Cmp) return undefined;
  // No `color` prop: the glyph inherits currentColor from `.fui-nav-section__icon` (the primary
  // green token), matching the live sidebar's group-icon tint.
  return <Cmp size={18} strokeWidth={2} aria-hidden="true" />;
}

// The classic Jmix app is served at the site root (the SPA sits behind Caddy under /new/**), so a
// not-yet-migrated item hands off via a root-absolute path to its Jmix @Route.
function legacyHref(route: string): string {
  return `/${route}`;
}

// jve-5-3: map the entry path to the initial page (tolerates the Caddy `/new` prefix).
/** Het dashboard van een werkmodus: het asieldashboard gaat over opvang, dus pension en crematorium hebben een eigen. */
export function dashboardVan(modus: Werkmodus): Page {
  return modus === "pension" ? "pension-dashboard" : modus === "crematorium" ? "crematorium-dashboard" : "dashboard";
}

/**
 * Waar de app begint: het scherm uit de url, maar het dashboard is dat van de werkmodus waarin je
 * werkt. Anders kom je na inloggen of verversen in pension op het asieldashboard uit, terwijl het
 * menu al de pensionadministratie toont.
 */
export function startpagina(modus: Werkmodus, pathname: string): Page {
  const pagina = initialPageFromPath(pathname);
  return pagina === "dashboard" ? dashboardVan(modus) : pagina;
}

export function initialPageFromPath(pathname: string): Page {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  // The root / empty path is the default landing: the dashboard (the top sidebar item, Jmix `/home`
  // parity). The spa-restore mirror rewrites "/" to /dashboard-react on entry.
  if (path === "" || path === "/") return "dashboard";
  // dashboard migration: a DISTINCT React path that does NOT collide with the legacy Jmix `/home` route.
  if (path.startsWith("/dashboard-react")) return "dashboard";
  if (path.startsWith("/pension-dashboard")) return "pension-dashboard";
  if (path.startsWith("/pension-overzichten")) return "pension-overzichten";
  if (path.startsWith("/pension-wegen")) return "pension-wegen";
  if (path.startsWith("/klantverzoeken")) return "klantverzoeken";
  if (path.startsWith("/klantberichten")) return "klantberichten";
  if (path.startsWith("/portaal-instellingen")) return "portaal-instellingen";
  if (path.startsWith("/pension-bodycheck")) return "pension-bodycheck";
  if (path.startsWith("/pension-borstelen")) return "pension-borstelen";
  if (path.startsWith("/pension-medicatie")) return "pension-medicatie";
  if (path.startsWith("/pension-voerlijst")) return "pension-voerlijst";
  if (path.startsWith("/service-accounts/me") || path.startsWith("/self-service")) return "self-service";
  if (path.startsWith("/service-accounts")) return "service-accounts";
  if (path.startsWith("/users")) return "users";
  if (path.startsWith("/voorkeuren")) return "voorkeuren";
  if (path.startsWith("/wassen-soorten")) return "wassen-soorten";
  if (path.startsWith("/asiel-tarief")) return "asiel-tarief";
  if (path.startsWith("/pension-arrangementen")) return "pension-arrangementen";
  if (path.startsWith("/pension-tarief")) return "pension-tarief";
  if (path.startsWith("/creche-tarief")) return "creche-tarief";
  if (path.startsWith("/dierenambulance-tarief")) return "dierenambulance-tarief";
  if (path.startsWith("/crematorium-handelingen")) return "crematorium-handelingen";
  if (path.startsWith("/crematorium-vergoedingen")) return "crematorium-vergoedingen";
  if (path.startsWith("/crematorium-tarief")) return "crematorium-tarief";
  if (path.startsWith("/crematorium-vervoer")) return "crematorium-vervoer";
  if (path.startsWith("/crematorium-dashboard")) return "crematorium-dashboard";
  if (path.startsWith("/crematorium-planning")) return "crematorium-planning";
  if (path.startsWith("/crematorium-runs")) return "crematorium-runs";
  if (path.startsWith("/crematorium-as-retour")) return "crematorium-asretour";
  if (path.startsWith("/crematorium-aanmeldingen")) return "aanmeldingen";
  // spa-restore: an open crematie dossier mirrors to /crematorium-dossier/:id (pathForShellState);
  // the bare path (no id) stays on the Crematies list it is reached from.
  if (/^\/crematorium-dossier\/[^/?#]/.test(path)) return "crematie-dossier";
  if (path.startsWith("/crematorium-crematies") || path.startsWith("/crematorium-dossier")) return "crematies";
  if (path.startsWith("/crematorium-facturen")) return "crematorium-facturen";
  if (path.startsWith("/producten")) return "producten";
  // invoices-migration Epic 6: the unified invoice browse. A /facturen/:id deep-link restores the
  // invoice detail (spa-restore — id parsed by initialFactuurIdFromPath, "Terug" seeded to the list).
  if (/^\/facturen\/[^/?#]/.test(path)) return "factuur";
  if (path.startsWith("/financieel-overzichten")) return "financieel-overzichten";
  if (path.startsWith("/losse-verkoop")) return "losse-verkoop";
  if (path.startsWith("/kasboek")) return "kasboek";
  if (path.startsWith("/dagafsluiting")) return "dagafsluiting";
  if (path.startsWith("/facturen")) return "facturen";
  if (path.startsWith("/animal-tags") || path.startsWith("/tags")) return "tags-hub";
  if (path.startsWith("/inboeken")) return "inboeken";
  if (path.startsWith("/uitboeken")) return "uitboeken";
  if (path.startsWith("/uitwisselingen") || path.startsWith("/shelter-exchanges")) return "uitwisselingen";
  // sidebar-menu-parity: the consolidated Opvangoverzichten bundle (Jmix @Route "opvang-overzichten").
  if (path.startsWith("/opvang-overzichten")) return "opvang-overzichten";
  if (path.startsWith("/dier-attendances")) return "dieren-aanwezig";
  if (path.startsWith("/dier-placements")) return "dieren-geplaatst";
  // pleeggezin-overzicht: same path as the Jmix @Route — no legacy handoff existed for it, so the
  // SPA claims it directly.
  if (path.startsWith("/pleeggezin-overzicht")) return "pleeggezin-overzicht";
  if (path.startsWith("/entingen-overzicht")) return "entingen-overzicht";
  // kp-Epic 2: a DISTINCT deep-link path for the React planner that does NOT collide with the legacy
  // Jmix `/kennelplanning` route (which still hands off to the classic app).
  if (path.startsWith("/planning-react")) return "kennelplanning";
  if (path.startsWith("/data-insight")) return "data-insight";
  if (path.startsWith("/schermbouwer-beheer")) return "schermbouwer-beheer";
  if (path.startsWith("/schermbouwer")) return "schermbouwer";
  if (path.startsWith("/ontwormen-overzicht")) return "ontwormen-overzicht";
  if (path.startsWith("/medicijn-overzicht")) return "medicijn-overzicht";
  // nazorg-beheer: a DISTINCT deep-link path that does NOT collide with the legacy Jmix `/nazorg` route
  // (which still hands off to the classic app for the wizard-builder).
  if (path.startsWith("/nazorg-react")) return "nazorg";
  if (path.startsWith("/animals") || path === "/animal" || path.startsWith("/animal/")) return "animals";
  if (path.startsWith("/relaties") || path.startsWith("/relatie/")) return "relaties";
  // modulebeheer (module on/off): a deep-link path so a refresh / bookmark lands on the screen instead
  // of the "under construction" fallback (it was reachable only via in-app nav state before).
  if (path.startsWith("/modulebeheer")) return "modulebeheer";
  // vestigingen-beheer: the SPA claims the same path as the Jmix @Route "vestigingen-beheer" (the legacy
  // handoff is gone now the React screen stands in for it).
  if (path.startsWith("/vestigingen-beheer")) return "vestigingen";
  // entity-admin (generic docasoft CRUD): off-menu, deep-link only — `/entity-admin` (catalog) or
  // `/entity-admin/{key}` (one entity), like wassen-soorten/tags. Not module-gated (kern-admin).
  if (path.startsWith("/entity-admin")) return "entity-admin";
  if (path.startsWith("/instellingen")) return "instellingen";
  // communicatie (read-foundation UI): a DISTINCT deep-link path that does NOT collide with the legacy
  // Jmix `/communicatie` (+ `/communicatie-log`) routes, which still hand off to the classic app.
  if (path.startsWith("/communicatie-react")) return "communicatie-react";
  // advertentie-migration: a DISTINCT deep-link path that does NOT collide with the legacy Jmix
  // `/advertenties` route (which still hands off to the classic app for the editor/builder/publish).
  if (path.startsWith("/advertenties-react")) return "advertenties";
  if (path.startsWith("/zoekresultaten")) return "zoekresultaten";
  // spa-session: any other path is a not-yet-migrated / unknown route — render the in-SPA
  // "under construction" placeholder instead of silently dropping the operator on the SA list.
  return "under-construction";
}

// spa-restore: the canonical URL path per page — the inverse of initialPageFromPath, used to mirror
// state navigation to the URL so a refresh / re-login restores the last opened screen (Jmix parity:
// Vaadin always keeps the open @Route in the URL, which is why the classic app "remembered" your
// place). Pages whose URL is id-dependent (the invoice/dossier details — mirrored by
// pathForShellState from their own id state) and the search results page (which owns its ?q= URL via
// navigateSearch) are deliberately absent — with no id open the URL simply stays where it was.
export const PAGE_PATH: Partial<Record<Page, string>> = {
  dashboard: "/dashboard-react",
  "pension-dashboard": "/pension-dashboard",
  "pension-overzichten": "/pension-overzichten",
  "pension-wegen": "/pension-wegen",
  "pension-bodycheck": "/pension-bodycheck",
  klantverzoeken: "/klantverzoeken",
  klantberichten: "/klantberichten",
  "portaal-instellingen": "/portaal-instellingen",
  "pension-borstelen": "/pension-borstelen",
  "pension-medicatie": "/pension-medicatie",
  "pension-voerlijst": "/pension-voerlijst",
  "service-accounts": "/service-accounts",
  users: "/users",
  "wassen-soorten": "/wassen-soorten",
  "self-service": "/self-service",
  producten: "/producten",
  "asiel-tarief": "/asiel-tarief",
  "pension-tarief": "/pension-tarief",
  "pension-arrangementen": "/pension-arrangementen",
  "creche-tarief": "/creche-tarief",
  "dierenambulance-tarief": "/dierenambulance-tarief",
  "crematorium-handelingen": "/crematorium-handelingen",
  "crematorium-vergoedingen": "/crematorium-vergoedingen",
  "crematorium-tarief": "/crematorium-tarief",
  "crematorium-vervoer": "/crematorium-vervoer",
  "crematorium-dashboard": "/crematorium-dashboard",
  "crematorium-planning": "/crematorium-planning",
  "crematorium-runs": "/crematorium-runs",
  "crematorium-asretour": "/crematorium-as-retour",
  aanmeldingen: "/crematorium-aanmeldingen",
  crematies: "/crematorium-crematies",
  "crematorium-facturen": "/crematorium-facturen",
  "entingen-overzicht": "/entingen-overzicht",
  "ontwormen-overzicht": "/ontwormen-overzicht",
  "medicijn-overzicht": "/medicijn-overzicht",
  nazorg: "/nazorg-react",
  animals: "/animals",
  "opvang-overzichten": "/opvang-overzichten",
  "dieren-aanwezig": "/dier-attendances",
  "dieren-geplaatst": "/dier-placements",
  "pleeggezin-overzicht": "/pleeggezin-overzicht",
  kennelplanning: "/planning-react",
  "data-insight": "/data-insight",
  schermbouwer: "/schermbouwer",
  "schermbouwer-beheer": "/schermbouwer-beheer",
  relaties: "/relaties",
  "financieel-overzichten": "/financieel-overzichten",
  kasboek: "/kasboek",
  "losse-verkoop": "/losse-verkoop",
  dagafsluiting: "/dagafsluiting",
  facturen: "/facturen",
  inboeken: "/inboeken",
  uitboeken: "/uitboeken",
  uitwisselingen: "/uitwisselingen",
  "tags-hub": "/animal-tags",
  instellingen: "/instellingen",
  modulebeheer: "/modulebeheer",
  vestigingen: "/vestigingen-beheer",
  "entity-admin": "/entity-admin",
  "communicatie-react": "/communicatie-react",
  advertenties: "/advertenties-react",
  voorkeuren: "/voorkeuren",
};

// spa-restore: the URL to mirror for the current shell state. An open detail gets its id in the
// path — /animal/:id · /relatie/:id (the forms the search + overzicht deep-links already use), plus
// /facturen/:id · /crematorium-dossier/:id for the invoice/dossier details — so a refresh restores
// the detail, not just its list. `detailId` is the open record's id for the given page (the caller
// passes factuurId / crematieDossierId for those pages). Null = don't touch the URL.
export function pathForShellState(page: Page, detailId: string | null): string | null {
  if (detailId != null) {
    if (page === "animals") return `/animal/${encodeURIComponent(detailId)}`;
    if (page === "relaties") return `/relatie/${encodeURIComponent(detailId)}`;
    if (page === "klantverzoeken") return `/klantverzoeken/${encodeURIComponent(detailId)}`;
    if (page === "klantberichten") return `/klantberichten/${encodeURIComponent(detailId)}`;
    if (page === "factuur") return `/facturen/${encodeURIComponent(detailId)}`;
    if (page === "crematie-dossier") return `/crematorium-dossier/${encodeURIComponent(detailId)}`;
  }
  return PAGE_PATH[page] ?? null;
}

/**
 * Whether the URL-mirroring effect should rewrite `current` to the canonical `target`.
 *
 * False when they already match (keeps a deep-link's query string intact), and false when the page
 * owns a deeper sub-route it must keep: `entity-admin` puts the open entity in the URL itself
 * (`/entity-admin/{key}` — `EntityAdminHub` reads it from `location.pathname`), so it is not
 * reproducible from `page` alone. Mirroring the bare `/entity-admin` there stripped the key and
 * bounced the screen back to the catalog, which made every per-entity screen unreachable — by deep
 * link AND by clicking a catalog entry.
 *
 * Deliberately NOT a general "current starts with target" rule: for a list page like `facturen` the
 * deeper `/facturen/:id` really must be rewritten back to `/facturen` once the detail closes,
 * otherwise a refresh would reopen a detail the operator had left.
 */
export function shouldMirrorPath(page: Page, current: string, target: string): boolean {
  if (current === target) return false;
  if (page === "entity-admin" && current.startsWith(`${target}/`)) return false;
  return true;
}

/**
 * The animal the operator last had open, so the "Dier" menu returns them to it instead of a list of
 * every animal in the shelter. Stored per browser like the vestiging choice; a missing or unreadable
 * value simply means "none yet" and the page shows its empty state.
 */
const LAST_ANIMAL_KEY = "fawkes.lastAnimalId";
/** Same for "Relaties": the menu returns to the relation you had open, never to a list of all of them. */
const LAST_RELATIE_KEY = "fawkes.lastRelatieId";

export function readLastAnimalId(): string | null {
  return readRemembered(LAST_ANIMAL_KEY);
}

export function rememberLastAnimalId(id: string | null): void {
  remember(LAST_ANIMAL_KEY, id);
}

export function readLastRelatieId(): string | null {
  return readRemembered(LAST_RELATIE_KEY);
}

export function rememberLastRelatieId(id: string | null): void {
  remember(LAST_RELATIE_KEY, id);
}

function readRemembered(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) || null;
  } catch {
    return null;
  }
}

function remember(key: string, id: string | null): void {
  try {
    if (id) globalThis.localStorage?.setItem(key, id);
    else globalThis.localStorage?.removeItem(key);
  } catch {
    // A blocked/full localStorage just costs us the convenience — never break navigation over it.
  }
}

// spa-restore: seed the animal/relatie detail id from the entry URL (refresh / deep-link restore).
// Tolerates the Caddy `/new` prefix and trailing query/fragment.
export function initialDetailIdFromPath(pathname: string): string | null {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  const m = /^\/(?:animal|relatie|klantverzoeken|klantberichten)\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
}

// spa-restore: seed the invoice detail id from a /facturen/:id entry URL (refresh / deep-link
// restore) — the invoice detail keeps its id in factuurId, separate from detailId.
export function initialFactuurIdFromPath(pathname: string): string | null {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  const m = /^\/facturen\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
}

// spa-restore: seed the crematie dossier id from a /crematorium-dossier/:id entry URL.
export function initialCrematieDossierIdFromPath(pathname: string): string | null {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  const m = /^\/crematorium-dossier\/([^/?#]+)/.exec(path);
  return m ? decodeURIComponent(m[1]) : null;
}

// search-migration E3: seed the results query/ai from the URL when entering on /zoekresultaten — a
// deep-link or the header handoff lands here. Tolerates the Caddy `/new` coexistence prefix.
export function initialSearchFromUrl(pathname: string, search: string): { query: string; ai: boolean } {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  if (!path.startsWith("/zoekresultaten")) return { query: "", ai: false };
  const params = new URLSearchParams(search);
  return { query: params.get("q") ?? "", ai: params.get("ai") === "1" };
}

// entingen-overzicht deep-link: seed the Bescherming-tile edit dialog from the entry URL. The
// ?tile=bescherming&inenting=<id> query that openAnimalFromOverzicht pushes is shareable, so a
// refresh / deep-link (and back/forward onto that history entry) must reopen the dialog too. Only
// honored on an /animal/:id path; tolerates the Caddy `/new` coexistence prefix.
export function initialVaccinationDeepLinkFromUrl(pathname: string, search: string): number | null {
  const path = pathname.replace(/^\/new(?=\/|$)/, "");
  if (!/^\/animal\/[^/?#]+/.test(path)) return null;
  const params = new URLSearchParams(search);
  if (params.get("tile") !== "bescherming") return null;
  const inenting = params.get("inenting");
  return inenting != null && /^\d+$/.test(inenting) ? Number(inenting) : null;
}

// password-reset: the anonymous /set-password consume page (token in the URL fragment) is reachable
// without a session — it must render BEFORE the <Authenticated> gate. Tolerates the Caddy `/new` prefix.
export function isSetPasswordPath(pathname: string): boolean {
  return pathname.replace(/^\/new(?=\/|$)/, "") === "/set-password";
}

// Sidebar profile card — resolves the signed-in identity from the token (jve-2-4 getIdentity).
// "Instellingen" routes to the migrated Settings dashboard (jve-settings); logout clears the token.
function SidebarProfile({ onSettings, onProfile }: { onSettings: () => void; onProfile: () => void }) {
  const { data: identity } = useGetIdentity<{ id: string; name?: string; email?: string }>();
  // De eigen profielfoto: de bytes zitten achter de bearer, dus ophalen en als object-URL doorgeven.
  // De dialoog meldt een wijziging, zodat je je nieuwe foto meteen ziet in plaats van na een herlaadbeurt.
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  useEffect(() => {
    let levend = true;
    let huidige: string | null = null;
    const laad = () => {
      void profielfotoUrl().then((url) => {
        if (!levend) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (huidige) URL.revokeObjectURL(huidige);
        huidige = url;
        setFotoUrl(url);
      });
    };
    laad();
    globalThis.addEventListener?.(FOTO_GEWIJZIGD, laad);
    return () => {
      levend = false;
      globalThis.removeEventListener?.(FOTO_GEWIJZIGD, laad);
      if (huidige) URL.revokeObjectURL(huidige);
    };
  }, []);
  // spa-logout: route through the auth provider so the server revokes the refresh-token family and
  // clears the HttpOnly cookie. A bare clearAccessToken()+reload left the cookie intact, so the
  // startup restoreSession() silently re-minted a token → "logged straight back in on the same animal".
  const { mutate: logout } = useLogout();
  // The token's `name` is the account's "Voornaam Achternaam" when it has one, else the bare login
  // name — and a login name is lower-case ("admin"), which reads as a typo in a profile card. Only
  // the display is capitalised; nothing is invented.
  const naam = identity?.name?.trim();
  const weergaveNaam = naam ? naam.charAt(0).toUpperCase() + naam.slice(1) : "Gebruiker";
  return (
    <ProfileCard
      name={weergaveNaam}
      email={identity?.email}
      avatarSrc={fotoUrl ?? undefined}
      settingsLabel="Instellingen"
      logoutLabel="Uitloggen"
      onSettings={onSettings}
      onProfile={onProfile}
      onLogout={() => logout()}
    />
  );
}

// jve-2-6: the app renders inside the AppShell (sidebar + header + working area). Navigation is
// state-based; list rows open a useOne-backed detail view in the working area. The page (+ the open
// animal/relatie/invoice/dossier detail id) is mirrored to the URL (spa-restore), so refresh
// restores the screen.
function AuthedApp() {
  const t = useTranslate();
  // Met welk onderdeel Pensionoverzichten opent als je vanuit Wegen … Voerlijst op Dagopvang (enz.) klikt.
  const [pensionOnderdeel, setPensionOnderdeel] = useState<PensionOnderdeel>("PENSION");
  // Modulebeheer gate for the sidebar (Jmix parity): items whose module is off disappear from the menu.
  const { isModuleEnabled } = useModuleAvailability();
  // Werkmodus (Asiel / Pension, picked in the vestiging card): rewrites the APPLICATIE section only.
  const [werkmodus] = useWerkmodus();
  // Pension of Hotel (Instellingen › Pension › Benaming): benaming.ts zet het woord in vertalingen en vaste
  // teksten om. Zonder antwoord (geen recht, oude api) blijft het Pension.
  const benaming = useCustom<{ benaming?: Benaming }>({
    url: "pension/benaming",
    method: "get",
    queryOptions: { retry: false, staleTime: 5 * 60_000 },
  });
  const benamingWaarde = benaming.data?.data?.benaming;
  useEffect(() => {
    zetBenaming(benamingWaarde);
  }, [benamingWaarde]);
  // Na inloggen op het dashboard beginnen; bij verversen blijven waar je was. De URL van een
  // verlopen sessie zegt niets meer over waar je heen wilde — je was daar toen je eruit vloog.
  //
  // Eén keer lezen en vasthouden: neemVerseAanmelding() wist de vlag, dus een tweede aanroep zou
  // false geven en dan zou het beginscherm het dashboard zijn terwijl de detail-id's nog uit de
  // oude URL kwamen — een dashboard met een dier eronder dat je niet had geopend.
  const [versAangemeld] = useState(neemVerseAanmelding);
  const [page, setPage] = useState<Page>(() =>
    versAangemeld ? dashboardVan(werkmodus) : startpagina(werkmodus, globalThis.location?.pathname ?? "/"),
  );
  const [detailId, setDetailId] = useState<string | null>(() =>
    versAangemeld ? null : initialDetailIdFromPath(globalThis.location?.pathname ?? "/"),
  );
  // Crematorium Epic 21 MR-2: the dossier opened in the read-only Crematies detail. Seeded from a
  // /crematorium-dossier/:id entry URL (spa-restore).
  const [crematieDossierId, setCrematieDossierId] = useState<string | null>(() =>
    versAangemeld ? null : initialCrematieDossierIdFromPath(globalThis.location?.pathname ?? "/"),
  );
  const [crematieDossierTerug, setCrematieDossierTerug] = useState<Page>("crematies");
  // Het dossier hoort onder de klant (tabblad Uitvaart). Vanaf een klant openen we het daar; het
  // losse dossierscherm blijft voor dossiers zonder klant en voor de lijsten, met een knop erheen.
  const [relatieInit, setRelatieInit] = useState<{ tab: "uitvaart"; dossierId: string | null } | null>(null);
  const openKlantUitvaart = (relatieId: number, dossierId: string | null) => {
    setRelatieInit({ tab: "uitvaart", dossierId });
    setDetailId(String(relatieId));
    setPage("relaties");
  };
  // invoices-migration Epic 1: the invoice opened in the read-only detail screen. Kept separate from
  // detailId so opening an invoice from a relation preserves the underlying relation detail to return to.
  // Seeded from a /facturen/:id entry URL (spa-restore).
  const [factuurId, setFactuurId] = useState<string | null>(() =>
    initialFactuurIdFromPath(globalThis.location?.pathname ?? "/"),
  );
  // invoices-migration Epic 6: the page "Terug" from the invoice detail should return to — the relation
  // detail ("relaties", the Facturen-tile / search default) or the unified invoice list ("facturen").
  // A URL-restored invoice detail returns to the list (the launching relation, if any, is not in the URL).
  const [factuurReturnTo, setFactuurReturnTo] = useState<Page>(() =>
    initialFactuurIdFromPath(globalThis.location?.pathname ?? "/") != null ? "facturen" : "relaties",
  );
  // search-migration E3: the active results-page query + ai flag (seeded from the URL on entry, set by
  // the header handoff). The URL is mirrored via pushState so /zoekresultaten?q=… is deep-linkable.
  const [searchInit] = useState(() =>
    initialSearchFromUrl(globalThis.location?.pathname ?? "/", globalThis.location?.search ?? ""),
  );
  const [searchQuery, setSearchQuery] = useState<string>(searchInit.query);
  const [searchAi, setSearchAi] = useState<boolean>(searchInit.ai);
  // Inboeken (intake) wizard: an optional preselected animal (launched from a detail tile) or null
  // (launched from the list — pick animals inside the wizard); or an existing residency id when
  // editing an intake.
  const [inboekenAnimalId, setInboekenAnimalId] = useState<string | null>(null);
  const [inboekenEditId, setInboekenEditId] = useState<string | null>(null);
  // Receiving-side intake of an exchanged animal (Epic 3 Story 10): completing the intake completes
  // the linked exchange. Launched from the Uitwisselingen inbox for an accepted internal exchange.
  const [inboekenExchangeId, setInboekenExchangeId] = useState<string | null>(null);
  // The linked exchange's source shelter (klant) — the wizard's preset brenger for a receive-side
  // intake (Jmix ShelterIntakePrepopulationService: bringer = sourceKlant).
  const [inboekenExchangeSource, setInboekenExchangeSource] = useState<{
    id: number | null;
    label: string | null;
  } | null>(null);
  // Uitboeken (release) wizard: an optional preselected animal (launched from the Verblijf UIT tile)
  // or null (launched from the list — pick the resident animal inside the wizard).
  const [uitboekenAnimalId, setUitboekenAnimalId] = useState<string | null>(null);
  // wizard-dialog-mode: launch actions now open the wizards as a Jmix-style dialog OVER the current
  // page (page/detail state stays put). The pre-existing full-page view remains: a deep link lands on
  // page "inboeken"/"uitboeken" directly, and the dialog's "open as full page" action switches to it.
  // Presentation is derived, not stored: page === wizard page ⇒ view, otherwise dialog (WizardHost
  // keeps the wizard mounted across that flip, preserving in-progress entries + the open draft).
  const [inboekenOpen, setInboekenOpen] = useState(false);
  const [uitboekenOpen, setUitboekenOpen] = useState(false);
  // Bumped when a wizard DIALOG completes: remounts the working area underneath so it refetches
  // (Jmix parity — the owning screen refreshes when a wizard dialog closes). A cancel doesn't bump.
  const [workAreaEpoch, setWorkAreaEpoch] = useState(0);
  // Entingen-/Ontwormen-overzicht "Bewerken" deep-link: the inenting record to open in the animal
  // detail's Bescherming tile edit dialog. Set together with the detail id; cleared on any other nav.
  // Seeded from the entry URL too (refresh / shared link), like the detail id and the search query.
  const [animalInitialTab, setAnimalInitialTab] = useState<"verblijf" | "advertentie" | null>(null);
  const [vaccinationDeepLinkId, setVaccinationDeepLinkId] = useState<number | null>(() =>
    initialVaccinationDeepLinkFromUrl(
      globalThis.location?.pathname ?? "/",
      globalThis.location?.search ?? "",
    ),
  );
  // Dev-only "Ontwikkeling" menu group (component/tile gallery): shown under `vite dev` and on a
  // deployed TEST instance only (devSurfacesEnabled), hidden on acc/production. Resolves to true
  // instantly in DEV; on a deployed build it does one cheap public /auth/config check.
  const [devVisible, setDevVisible] = useState<boolean>(import.meta.env.DEV);
  useEffect(() => {
    let live = true;
    void devSurfacesEnabled().then((enabled) => {
      if (live) setDevVisible(enabled);
    });
    return () => {
      live = false;
    };
  }, []);

  // nazorg-authoring: NazorgBeheer's Vragenlijsten tab navigates in-SPA to the settings
  // wizard-builder — opened on an existing definition, or creating a fresh nazorg-vragenlijst.
  // Consumed once by SettingsDashboard; cleared on any regular navigation so the sidebar's
  // "Instellingen" opens the plain dashboard.
  const [wizardBuilderInit, setWizardBuilderInit] = useState<WizardBuilderInit | null>(null);
  // "Nieuwe relatie" from the empty relation screen (the list is no longer a destination).
  const [nieuweRelatie, setNieuweRelatie] = useState(false);
  // De aangeklikte nazorginzending; null is het beheerscherm zelf.
  const [nazorgInzendingId, setNazorgInzendingId] = useState<string | null>(null);
  // "Mijn voorkeuren" is op acc een dialoog over het scherm waar je stond, geen eigen pagina; de route
  // /voorkeuren opent hem alleen (en verdwijnt uit de URL zodra hij dicht gaat).
  const [voorkeurenOpen, setVoorkeurenOpen] = useState(() => page === "voorkeuren");

  const select = (p: Page) => {
    setPage(p);
    // "Dier" reopens whatever animal you last had open — the list is not the destination, a specific
    // animal is (and the shell's search covers finding a different one). "Relaties" works the same way.
    // With nothing remembered the page falls through to its own empty state.
    setDetailId(p === "animals" ? readLastAnimalId() : p === "relaties" ? readLastRelatieId() : null);
    setVaccinationDeepLinkId(null);
    setAnimalInitialTab(null);
    setWizardBuilderInit(null);
  };

  const openVragenlijstBuilder = (init: WizardBuilderInit) => {
    setDetailId(null);
    setVaccinationDeepLinkId(null);
    setWizardBuilderInit(init);
    setPage("instellingen");
  };

  // dashboard migration: dispatch a Jmix route name (the dashboard's KPI/link/workspace targets) to the
  // migrated in-SPA page where one exists, else hand off to the legacy Jmix route (root-absolute href),
  // exactly like the legacy nav items + the search quick-actions do.
  const navigateToRoute = (route: string) => {
    switch (route) {
      case "animal":
        return select("animals");
      case "relaties":
        return select("relaties");
      case "facturen":
        return select("facturen");
      case "dier-attendances":
        return select("dieren-aanwezig");
      case "dier-placements":
        return select("dieren-geplaatst");
      case "entingen-overzicht":
        return select("entingen-overzicht");
      case "uitwisselingen":
        return select("uitwisselingen");
      case "instellingen":
        return select("instellingen");
      default:
        // kennelplanning (the Jmix planner), … have no migrated SPA page yet.
        globalThis.location?.assign(legacyHref(route));
    }
  };

  // Open the animal detail from a status-overview row. "Bewerken" additionally passes the displayed
  // record's inenting id so the Bescherming tile opens its edit dialog on that record; "Details" (no
  // id) just opens the detail. The deep-link is mirrored to the URL (consistent with the search flow)
  // so the chosen tile + record are visible/shareable.
  // A stay link from a relation opens the animal on its Verblijf tab, the way Jmix does
  // (/animals/{id}?tab=verblijf). Cleared on any other navigation so it never sticks.
  // Vanuit de pensionoverzichten opent een dier (of zijn klant) het klantscherm van het pension, met
  // dat dier gekozen — niet het asieldossier. Zonder klant valt het terug op het dier zelf.
  const [pensionKlantDier, setPensionKlantDier] = useState<number | null>(null);
  const openPensionKlant = (klantId: number, dierId: number | null) => {
    // Eerst select: die zet zelf de laatst bekeken relatie als detailId, en zou de klant anders overschrijven.
    select("relaties");
    setDetailId(String(klantId));
    setPensionKlantDier(dierId);
  };

  const openAnimalOpVerblijf = (id: string) => {
    setAnimalInitialTab("verblijf");
    setVaccinationDeepLinkId(null);
    setDetailId(id);
    setPage("animals");
  };

  // Vanuit het advertentieoverzicht open je het hele dier, niet alleen zijn advertentie: je wilt er
  // toch bij kunnen wat er verder over bekend is. Het advertentietabblad staat dan al open.
  const openAnimalOpAdvertentie = (dierId: string) => {
    setAnimalInitialTab("advertentie");
    setVaccinationDeepLinkId(null);
    setDetailId(dierId);
    setPage("animals");
  };

  const openAnimalFromOverzicht = (id: string, inentingId?: number | null) => {
    setAnimalInitialTab(null);
    setVaccinationDeepLinkId(inentingId ?? null);
    setDetailId(id);
    setPage("animals");
    if (inentingId != null) {
      const prefix = (globalThis.location?.pathname ?? "/").startsWith("/new") ? "/new" : "";
      globalThis.history?.pushState({}, "", `${prefix}/animal/${id}?tile=bescherming&inenting=${inentingId}`);
    }
  };

  // search-migration E4 (FR23): browser back/forward. The only history entries the shell pushes come
  // from the search navigation (navigateSearch/goToResults) and the overzicht "Bewerken" deep-link, so
  // popstate re-derives the page + the results query/ai from the URL. The detail ids (animal/relatie,
  // invoice, crematie dossier) are re-derived too (spa-restore), so a back onto a pushed entry with a
  // detail path reopens that detail.
  useEffect(() => {
    const onPop = () => {
      const path = globalThis.location?.pathname ?? "/";
      const p = startpagina(readWerkmodus(), path);
      setDetailId(initialDetailIdFromPath(path));
      const factuur = initialFactuurIdFromPath(path);
      setFactuurId(factuur);
      // The launching context is gone on a history jump — "Terug" falls back to the invoice list.
      if (factuur != null) setFactuurReturnTo("facturen");
      setCrematieDossierId(initialCrematieDossierIdFromPath(path));
      setVaccinationDeepLinkId(
        initialVaccinationDeepLinkFromUrl(path, globalThis.location?.search ?? ""),
      );
      if (p === "zoekresultaten") {
        const s = initialSearchFromUrl(path, globalThis.location?.search ?? "");
        setSearchQuery(s.query);
        setSearchAi(s.ai);
      }
      setPage(p);
    };
    globalThis.addEventListener?.("popstate", onPop);
    return () => globalThis.removeEventListener?.("popstate", onPop);
  }, []);

  // spa-restore: mirror the shell state to the URL so a refresh / logout→login lands on the same
  // screen — the parity answer to Jmix keeping its @Route in the URL. replaceState, not pushState:
  // no new history entries, so the existing back/forward contract (search + inenting deep-links
  // only) is unchanged. Skips when the path already matches — that keeps the query string of a
  // deep-link (?tile=… / ?q=…) intact — and when the page has no URL of its own (pathForShellState
  // null: search results, under-construction), leaving the host URL in place. The invoice /
  // crematie-dossier details mirror from their own id state (pathForShellState's id slot).
  useEffect(() => {
    const loc = globalThis.location;
    if (!loc) return;
    const target = pathForShellState(
      page,
      page === "factuur" ? factuurId : page === "crematie-dossier" ? crematieDossierId : detailId,
    );
    if (!target) return;
    const prefix = loc.pathname.startsWith("/new") ? "/new" : "";
    const current = loc.pathname.replace(/^\/new(?=\/|$)/, "");
    if (!shouldMirrorPath(page, current, target)) return;
    globalThis.history?.replaceState({}, "", `${prefix}${target}`);
  }, [page, detailId, factuurId, crematieDossierId]);

  // Remember the animal on screen, whichever route opened it (menu, search, a tile, a deep link).
  useEffect(() => {
    if (page === "animals" && detailId) rememberLastAnimalId(detailId);
    if (page === "relaties" && detailId) rememberLastRelatieId(detailId);
  }, [page, detailId]);

  // search-migration E4 (FR23 / Decision URL): mirror a full search/filter state to the URL + shell
  // state. `search` is the complete query string (incl. q and ai=1 when present); an empty string clears
  // to a bare /zoekresultaten (the AI panel's close action / a removed query chip). The results page
  // calls this on every filter mutation so /zoekresultaten?... stays deep-linkable.
  const navigateSearch = (search: string, q: string, aiFlag: boolean) => {
    const prefix = (globalThis.location?.pathname ?? "/").startsWith("/new") ? "/new" : "";
    const url = search ? `${prefix}/zoekresultaten?${search}` : `${prefix}/zoekresultaten`;
    globalThis.history?.pushState({}, "", url);
    setSearchQuery(q.trim());
    setSearchAi(aiFlag);
    setDetailId(null);
    setPage("zoekresultaten");
  };

  // search-migration E3 (FR15): the header bar (Enter / footer / AI "Toepassen") navigates to the full
  // results page (plain or ai=1). Guards empty input; delegates the URL/state plumbing to navigateSearch.
  const goToResults = (q: string, ai: boolean) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const enc = encodeURIComponent(trimmed);
    navigateSearch(ai ? `ai=1&q=${enc}` : `q=${enc}`, trimmed, ai);
  };

  // search-migration E4 (FR17): the four "Snelle acties". animals/relaties have a migrated SPA screen
  // (state nav); facturen/verblijven have none yet, so they hand off to the legacy Jmix route (like the
  // legacy nav items). Maps the Jmix animals/new · relaties · facturen · verblijven quick-action routes.
  // De wegwijzer geeft een pad terug ("/asiel-tarief"); dezelfde afbeelding die een deep-link
  // gebruikt zet dat om naar een scherm, zodat er geen tweede lijst met paden ontstaat die uit
  // elkaar kan lopen.
  // De lopende rondleiding. Hier en niet in het zoekscherm: de rondleiding brengt je juist naar
  // andere schermen, en dan is het zoekscherm er niet meer.
  const [rondleiding, setRondleiding] = useState<string | null>(null);

  const openSearchRoute = (route: string) => {
    select(initialPageFromPath(route));
  };

  const onSearchQuickAction = (action: "animal" | "relation" | "invoice" | "stay") => {
    if (action === "animal") select("animals");
    else if (action === "relation") select("relaties");
    else globalThis.location?.assign(legacyHref(action === "invoice" ? "facturen" : "verblijven"));
  };

  // search-migration E3 (FR15): a result-card click opens the entity detail via state nav using the
  // structured {type,id} target (Decision NAV). Animals/relations have a migrated detail; an invoice or
  // an animal-less stay has none yet, so it falls back to the full results page (stays in-SPA).
  const openSearchHit = (targetType: string, targetId: string, fallbackQuery: string) => {
    setVaccinationDeepLinkId(null);
    if (targetType === "animal") {
      setPage("animals");
      setDetailId(targetId);
    } else if (targetType === "relation") {
      setPage("relaties");
      setDetailId(targetId);
    } else if (targetType === "invoice") {
      // invoices-migration Epic 1: an invoice search hit opens the read-only React invoice detail.
      setFactuurReturnTo("relaties");
      setFactuurId(targetId);
      setPage("factuur");
    } else {
      goToResults(fallbackQuery, false);
    }
  };

  /**
   * Een factuur die nog gemaakt moet worden: voor wie hij komt, meer niet.
   *
   * <p>Vroeger maakte "Nieuwe factuur" er meteen een aan op de server en opende die. Een misklik
   * liet dus een lege factuur achter in de boekhouding (Maarten, 2026-10-24). Nu opent het
   * factuurscherm leeg en ontstaat de factuur pas bij Opslaan.
   */
  const [factuurNieuw, setFactuurNieuw] = useState<{ klantId: number | null } | null>(null);
  const nieuweFactuurVoor = (klantId: string) => {
    setFactuurId(null);
    setFactuurNieuw({ klantId: Number(klantId) });
    setPage("factuur");
  };

  // invoices-migration Epic 1: open the read-only invoice detail (from the Relaties Facturen tile),
  // preserving the current relation detail so "Terug" returns to it.
  const openFactuur = (id: string) => {
    setFactuurReturnTo("relaties");
    setFactuurNieuw(null);
    setFactuurId(id);
    setPage("factuur");
  };
  // invoices-migration Epic 6: open the invoice detail from the unified list; "Terug" returns to it.
  const openFactuurFromList = (id: string) => {
    setFactuurReturnTo("facturen");
    setFactuurNieuw(null);
    setFactuurId(id);
    setPage("factuur");
  };
  // Terug vanaf een factuur gaat naar het scherm waar je vandaan kwam, wat dat ook was: het financieel overzicht,
  // de zoekbalk vanaf het dashboard, een klant (Maarten, 2026-10-14). Vaste bestemmingen per opener klopten niet
  // altijd: vanuit Financieel › Overzichten ging je terug naar de factuurlijst.
  const vorigePagina = useRef<Page>(page);
  useEffect(() => {
    if (page === "factuur" && vorigePagina.current !== "factuur") setFactuurReturnTo(vorigePagina.current);
    vorigePagina.current = page;
  }, [page]);
  const leaveFactuur = () => {
    setFactuurId(null);
    setFactuurNieuw(null);
    setPage(factuurReturnTo);
  };
  // Crematorium Epic 21 MR-2: open het dossier. "Terug" gaat naar het scherm waar je vandaan
  // kwam — vanuit de tegel Crematies op een klant is dat die klant, niet de crematielijst.
  // Vanaf welke klant de aanmeldwizard geopend is: zijn gegevens vullen de stap Eigenaar en het
  // nieuwe dossier komt aan hem te hangen. Leeg = een losse aanmelding.
  const [wizardKlant, setWizardKlant] = useState<CrematieWizardKlant | null>(null);
  const [crematieWizardOpen, setCrematieWizardOpen] = useState(false);
  const startCrematieWizard = (klant?: CrematieWizardKlant) => {
    setWizardKlant(klant ?? null);
    setCrematieWizardOpen(true);
  };

  const leaveCrematieWizard = () => {
    const vanafKlant = wizardKlant;
    const alsPagina = page === "crematie-aanmelding";
    setWizardKlant(null);
    setCrematieWizardOpen(false);
    if (!alsPagina) return;
    if (vanafKlant) {
      setDetailId(String(vanafKlant.relatieId));
      setPage("relaties");
    } else {
      setPage("aanmeldingen");
    }
  };

  /**
   * Een uitvaart openen. Het dossier hoort onder zijn klant (mockup: kop met tabbladen Eigenaar ·
   * Uitvaart · Documenten · Historie), dus daar komt hij terecht zodra de klant bekend is. Een
   * dossier zonder klant — een losse aanmelding van vroeger — heeft geen kop om onder te hangen
   * en houdt het eigen scherm.
   */
  const openCrematieDossier = (id: string, terugNaar: Page = "crematies", relatieId?: number | null) => {
    if (relatieId != null) {
      openKlantUitvaart(relatieId, id);
      return;
    }
    setCrematieDossierTerug(terugNaar);
    setCrematieDossierId(id);
    setPage("crematie-dossier");
  };
  const leaveCrematieDossier = () => {
    setCrematieDossierId(null);
    setPage(crematieDossierTerug);
  };

  // wizard-dialog-mode: launching opens the dialog over the current page — page + detail stay put,
  // so closing the dialog puts the operator back exactly where they were (Jmix dialog parity).
  const startInboeken = (animalId?: string | null) => {
    setInboekenAnimalId(animalId ?? null);
    setInboekenEditId(null);
    setInboekenExchangeId(null);
    setInboekenExchangeSource(null);
    setInboekenOpen(true);
  };
  const startInboekenEdit = (verblijfId: string) => {
    setInboekenAnimalId(null);
    setInboekenEditId(verblijfId);
    setInboekenExchangeId(null);
    setInboekenExchangeSource(null);
    setInboekenOpen(true);
  };
  // Receiving-side intake from an accepted exchange: preseed the animal, the exchange to complete,
  // and its source shelter (the wizard presets type = Uitwisseling + brenger = source).
  const startInboekenFromExchange = (
    animalId: string,
    exchangeId: string,
    source: { id: number | null; label: string | null },
  ) => {
    setInboekenAnimalId(animalId);
    setInboekenEditId(null);
    setInboekenExchangeId(exchangeId);
    setInboekenExchangeSource(source);
    setInboekenOpen(true);
  };
  const leaveInboeken = (completed: boolean, animalIds?: number[]) => {
    const wasExchangeIntake = inboekenExchangeId != null;
    const wasEdit = inboekenEditId != null;
    setInboekenAnimalId(null);
    setInboekenEditId(null);
    setInboekenExchangeId(null);
    setInboekenExchangeSource(null);
    setInboekenOpen(false);
    // Completion navigation (wizard-parity audit §3 row 23, Jmix post-finish): one animal → its
    // detail, several → the aanwezigheidsoverzicht. Not for edits (the operator came from the
    // detail underneath) or exchange intakes (back to the inbox — the completed exchange drops off).
    if (completed && !wasEdit && !wasExchangeIntake && animalIds && animalIds.length > 0) {
      if (animalIds.length === 1) {
        setVaccinationDeepLinkId(null);
        setDetailId(String(animalIds[0]));
        setPage("animals");
      } else {
        select("dieren-aanwezig");
      }
      return;
    }
    if (page === "inboeken") {
      // full-page view: navigate away, as before — a receiving-side intake returns to the exchange
      // inbox (the completed exchange drops off it)
      select(wasExchangeIntake ? "uitwisselingen" : "animals");
    } else if (completed) {
      // dialog: stay on the page underneath; remount it so it refetches (owner-screen refresh)
      setWorkAreaEpoch((e) => e + 1);
    }
  };

  const startUitboeken = (animalId?: string | null) => {
    setUitboekenAnimalId(animalId ?? null);
    setUitboekenOpen(true);
  };
  const leaveUitboeken = (completed: boolean, animalIds?: number[]) => {
    setUitboekenAnimalId(null);
    setUitboekenOpen(false);
    // Na een geslaagde uitboeking sta je op het dier dat je zojuist afhandelde — bij meerdere dieren
    // het laatste uit de lijst. Anders dan bij inboeken, dat bij meerdere dieren naar het
    // aanwezigheidsoverzicht springt: een uitgeboekt dier staat daar niet meer op.
    if (completed && animalIds && animalIds.length > 0) {
      setVaccinationDeepLinkId(null);
      setDetailId(String(animalIds[animalIds.length - 1]));
      setPage("animals");
      return;
    }
    if (page === "uitboeken") select("animals");
    else if (completed) setWorkAreaEpoch((e) => e + 1);
  };

  // Rood rondje achter Klantverzoeken: zoveel nieuwe verzoeken wachten op akkoord. Zonder portaal (of
  // zonder rechten) faalt de telling en blijft het rondje gewoon weg.
  const verzoekTelling = useCustom<{ nieuw?: number }>({
    url: "klantverzoeken/telling",
    method: "get",
    queryOptions: { enabled: werkmodus === "pension", refetchInterval: 60_000, retry: false },
  });
  // En achter Klantberichten: zoveel gesprekken wachten op een antwoord.
  const berichtTelling = useCustom<{ open?: number }>({
    url: "klantberichten/telling",
    method: "get",
    queryOptions: { enabled: werkmodus === "pension", refetchInterval: 60_000, retry: false },
  });
  const navBadges: Partial<Record<Page, number>> = {
    klantverzoeken: verzoekTelling.data?.data?.nieuw ?? 0,
    klantberichten: berichtTelling.data?.data?.open ?? 0,
  };

  const nav = visibleNav(isModuleEnabled, werkmodus).map((group) => {
    // Live sidebar sizing (frontend-react/sidebar Sidebar.jsx): top-level APPLICATIE items 19px,
    // items inside collapsible groups 16px.
    const itemSize = group.mode === "static" ? 19 : 16;
    return (
      <NavSection
        key={group.titleKey}
        label={t(group.titleKey)}
        defaultOpen={group.mode === "static" ? undefined : group.mode === "open"}
        icon={sectionGlyphNode(group.sectionGlyph)}
      >
        {group.items.map((item) =>
          item.kind === "spa" ? (
            <NavItem
              key={item.page}
              label={t(item.labelKey)}
              icon={glyphNode(item.glyph, itemSize)}
              active={page === item.page || (item.page === "pension-overzichten" && isPensionModulePage(page))}
              badge={navBadges[item.page]}
              onClick={() => select(item.page)}
            />
          ) : (
            <NavItem
              key={item.route}
              label={t(item.labelKey)}
              icon={glyphNode(item.glyph, itemSize)}
              href={legacyHref(item.route)}
              legacy
              title={`${t(item.labelKey)} — ${t("menu.legacyHint")}`}
            />
          ),
        )}
      </NavSection>
    );
  });

  // Dev-only "Ontwikkeling" group: full-page links to the gallery render-roots (separate from the
  // SPA `Page` state). Appended only when devVisible (DEV or a deployed TEST instance). The href
  // tolerates the Caddy `/new` coexistence prefix the same way main.tsx strips it before matching.
  const galleryBase = (globalThis.location?.pathname ?? "/").startsWith("/new") ? "/new" : "";
  const devNav = devVisible ? (
    <NavSection
      key="menu.devTools.title"
      label={t("menu.devTools.title")}
      defaultOpen={false}
      icon={sectionGlyphNode("wrench")}
    >
      <NavItem label={t("menu.gallery.components")} href={`${galleryBase}/gallery`} />
      <NavItem label={t("menu.gallery.tiles")} href={`${galleryBase}/tiles`} />
    </NavSection>
  ) : null;

  // search-migration E3 (FR14): the real global-search dropdown, replacing the disabled placeholder.
  // `syncQuery` fills the bar from the results-page query (Jmix syncQueryFromUrl); off the results page
  // it is undefined so the bar is left as-is.
  const header = (
    <GlobalSearch
      syncQuery={page === "zoekresultaten" ? searchQuery : undefined}
      onNavigateResults={goToResults}
      onOpenHit={openSearchHit}
    />
  );

  // Vestiging (branch) selector — real, data-driven (jve-vestiging): the user's accessible branches
  // with a working switch dialog, wired to /api/vestigingen.
  // Switching vestiging changes what every screen is scoped to, so reset to the home/landing page
  // (same source of truth as the initial load) instead of leaving the previous, now-stale screen up.
  const locationSelector = (
    <VestigingSwitcher
      onModusSwitched={(modus) =>
        select(
          modus === "pension"
            ? "pension-dashboard"
            : modus === "crematorium"
              ? "crematorium-dashboard"
              : "dashboard",
        )
      }
      onSwitched={() => {
        // The remembered animal/relation belongs to the branch you just left, so "Dier"/"Relaties"
        // would reopen a record from the previous administration. Forget them along with the screen.
        rememberLastAnimalId(null);
        rememberLastRelatieId(null);
        setFactuurId(null);
        setCrematieDossierId(null);
        select(startpagina(readWerkmodus(), "/"));
      }}
    />
  );

  // The full shell chrome is shared between the normal render and the module-disabled state below, so
  // the operator keeps the (already module-filtered) sidebar to navigate away from a blocked deep link.
  const shellProps = {
    // Per-screen density parity: acc still renders these detail/form views in the 80% Vaadin shell,
    // while the migrated React-module screens (dier-aanwezig, the overviews, product/vestigingen/wizard,
    // which run as 100% React iframes on acc) stay full-size. See DENSE_PAGES.
    // Uitzondering: het pension-klantscherm is geen Vaadin-pariteit maar een nieuw ontwerp, op ware
    // grootte getekend. Op 80% wordt elke maat uit dat ontwerp een vijfde kleiner.
    contentDense: DENSE_PAGES.has(page) && !(page === "relaties" && werkmodus === "pension" && detailId),
    brandTitle: "Docasoft",
    // Het huismerk zelf, niet langer het generieke pootje op een groen verloop dat uit de Jmix-schil
    // kwam. Het app-icoon draagt zijn eigen limegroene vlak; de schil laat zijn verloop daarom weg
    // zodra er een afbeelding staat (zie .fui-shell__brand-logo:has(img) in ui.css).
    // Ingeklapt blijft alleen dit teken over; het app-icoon met zijn gekleurde vlak is daar te luid
    // naast een kolom rustige menupictogrammen.
    brandLogoSrc: "/brand/docasoft-symbol-navy.svg",
    // Uitgeklapt het hele woordmerk; ingeklapt valt het weg en blijft het teken hierboven over.
    brandWordmarkSrc: "/brand/docasoft-logo-green.svg",
    title: t(PAGE_TITLE_KEY[page]),
    header,
    locationSelector,
    nav: devNav ? [...nav, devNav] : nav,
    sidebarFooter: (
      <SidebarProfile onSettings={() => select("instellingen")} onProfile={() => setVoorkeurenOpen(true)} />
    ),
  };

  // Deep-link module guard: the sidebar hides a disabled module's entries (visibleNav), but a
  // bookmarked/typed URL still lands on the page state — render the shared module-disabled empty state
  // instead of the page (mirroring the Kennelplanning/NazorgBeheer in-page gates, which stay as
  // defense-in-depth). Permissive while the feed loads, so a blip never blanks the working area.
  if (!isPageModuleEnabled(page, isModuleEnabled, werkmodus)) {
    return (
      <AppShell {...shellProps}>
        <div className="app-module-disabled">{t("app.moduleDisabled")}</div>
      </AppShell>
    );
  }

  return (
    <AppShell {...shellProps}>
      {/* wizard-dialog-mode: the epoch key remounts the working area when a wizard dialog completes,
          so the screen underneath refetches. The WizardHosts live OUTSIDE this fragment, at a stable
          tree position, so the dialog ⇄ full-page flip never remounts a mounted wizard. */}
      <Fragment key={workAreaEpoch}>
      {page === "dashboard" && (
        <Dashboard
          onNavigate={navigateToRoute}
          onNewAnimal={() => select("animals")}
          onInboeken={() => startInboeken(null)}
          onUitboeken={() => startUitboeken(null)}
          onOpenAnimal={(dierId) => {
            setVaccinationDeepLinkId(null);
            setDetailId(dierId);
            setPage("animals");
          }}
        />
      )}

      {page === "pension-dashboard" && <PensionDashboard onNavigate={select} />}

      {page === "pension-overzichten" && (
        <PensionOverzichten
          key={pensionOnderdeel}
          startOnderdeel={pensionOnderdeel}
          onOpenLijst={(p) => {
            setPensionOnderdeel("PENSION");
            select(p);
          }}
          onOpenPlanning={() => setPage("kennelplanning")}
          onOpenDier={(dierId, klantId) =>
            klantId != null ? openPensionKlant(klantId, dierId) : openAnimalOpVerblijf(String(dierId))
          }
          onOpenKlant={(klantId, dierId) => openPensionKlant(klantId, dierId ?? null)}
        />
      )}

      {page === "klantverzoeken" &&
        (detailId ? (
          <KlantverzoekDetail
            id={detailId}
            onBack={() => setDetailId(null)}
            onOpenKlant={(klantId) => {
              select("relaties");
              setDetailId(String(klantId));
            }}
          />
        ) : (
          <Klantverzoeken onOpen={setDetailId} onInstellingen={() => select("portaal-instellingen")} />
        ))}

      {page === "klantberichten" &&
        (detailId ? (
          <KlantberichtDetail
            id={detailId}
            onBack={() => setDetailId(null)}
            onOpenKlant={(klantId) => {
              select("relaties");
              setDetailId(String(klantId));
            }}
          />
        ) : (
          <Klantberichten onOpen={setDetailId} />
        ))}

      {page === "portaal-instellingen" && <PortaalInstellingen />}

      {/* Wegen … Voerlijst staan in de onderdelenbalk van Pensionoverzichten; boven die pagina's dezelfde balk. */}
      {isPensionLijstPage(page) && (
        <PensionLijstBalk
          actief={page}
          onLijst={select}
          onOnderdeel={(o) => {
            setPensionOnderdeel(o);
            select("pension-overzichten");
          }}
        />
      )}

      {page === "pension-wegen" && (
        <PensionWegen onOpenDier={(dierId) => openAnimalOpVerblijf(String(dierId))} />
      )}

      {page === "pension-bodycheck" && (
        <PensionBodycheck onOpenDier={(dierId) => openAnimalOpVerblijf(String(dierId))} />
      )}

      {page === "pension-borstelen" && (
        <PensionBorstelen onOpenDier={(dierId) => openAnimalOpVerblijf(String(dierId))} />
      )}

      {page === "pension-medicatie" && (
        <PensionMedicatie onOpenDier={(dierId) => openAnimalOpVerblijf(String(dierId))} />
      )}

      {page === "pension-voerlijst" && (
        <PensionVoerlijst onOpenDier={(dierId) => openAnimalOpVerblijf(String(dierId))} />
      )}

      {page === "service-accounts" &&
        (detailId ? (
          <ServiceAccountDetail id={detailId} onBack={() => setDetailId(null)} />
        ) : (
          <ServiceAccountList onOpen={setDetailId} onCreate={() => select("new-service-account")} />
        ))}

      {page === "users" &&
        (detailId ? (
          <UserDetail id={detailId} onBack={() => setDetailId(null)} />
        ) : (
          <UserList onOpen={setDetailId} onCreate={() => select("new-user")} />
        ))}

      {page === "new-user" && (
        <UserCreate onDone={() => select("users")} onCancel={() => select("users")} />
      )}

      {page === "modulebeheer" && <ModulebeheerScreen />}
      {page === "vestigingen" && <VestigingenBeheer />}
      {page === "entity-admin" && <EntityAdminHub />}

      {page === "wassen-soorten" &&
        (detailId ? (
          <WassenSoortDetail id={detailId} onBack={() => setDetailId(null)} />
        ) : (
          <WassenSoortList onOpen={setDetailId} />
        ))}

      {page === "new-service-account" && (
        <ServiceAccountCreateWizard onDone={() => select("service-accounts")} />
      )}

      {page === "self-service" && <ServiceAccountSelfService />}

      {page === "producten" && <ProductsOverview />}

      {page === "asiel-tarief" && <AsielTariefOverview />}

      {page === "pension-tarief" && <PensionTariefOverview />}

      {page === "pension-arrangementen" && <PensionArrangementen />}

      {page === "creche-tarief" && <CrecheTariefOverview />}

      {page === "dierenambulance-tarief" && <DierenambulanceTariefOverview />}

      {page === "crematorium-handelingen" && <CrematoriumHandelingOverview />}
      {page === "crematorium-vergoedingen" && (
        <CrematoriumVergoedingen
          onOpenRelatie={(id) => {
            // setPage, niet select: select() zet het id terug op de laatst onthouden relatie.
            setDetailId(String(id));
            setPage("relaties");
          }}
        />
      )}
      {page === "crematorium-tarief" && <CrematoriumTariefOverview />}

      {page === "crematorium-vervoer" && <CrematoriumVervoerOverview />}

      {page === "crematorium-dashboard" && (
        <CrematoriumDashboard
          onNewAanmelding={() => startCrematieWizard()}
          onRelaties={() => select("relaties")}
          onPlanning={() => select("crematorium-planning")}
          onAsRetour={() => select("crematorium-asretour")}
          onOpenAanmeldingen={() => select("aanmeldingen")}
          onOpenDossiers={() => select("crematies")}
          onOpenFacturen={() => select("crematorium-facturen")}
        />
      )}
      {page === "crematorium-planning" && <CrematoriumPlanningList />}

      {page === "crematorium-runs" && <CrematoriumRunsList />}

      {page === "crematorium-asretour" && <CrematoriumAsRetourList />}
      {page === "aanmeldingen" && (
        // Crematorium Epic 21 MR-W4: "Nieuwe aanmelding" now opens the React create-wizard (the Jmix
        // wizard stays reachable by its @Route during the strangler).
        <AanmeldingenList onNew={() => startCrematieWizard()} />
      )}
      {page === "crematies" && (
        <CrematoriumUitvaartOverzichten onOpenDossier={(id, relatieId) => openCrematieDossier(id, "crematies", relatieId)} />
      )}

      {page === "crematie-dossier" && crematieDossierId && (
        <CrematieDossierDetail
          id={crematieDossierId}
          onBack={leaveCrematieDossier}
          onOpenKlant={(relatieId) => openKlantUitvaart(relatieId, crematieDossierId)}
          // Een deeplink naar een dossier van een bekende klant hoort ook bij die klant te openen.
          verhuisNaarKlant={(relatieId) => openKlantUitvaart(relatieId, crematieDossierId)}
        />
      )}
      {page === "crematorium-facturen" && <CrematoriumFacturenList />}

      {page === "animals" &&
        (detailId ? (
          <AnimalDetail
            id={detailId}
            initialTab={animalInitialTab ?? undefined}
            onBack={() => setDetailId(null)}
            onOpenAnimal={(nextId) => {
              setVaccinationDeepLinkId(null);
              setDetailId(nextId);
            }}
            onStartInboeken={startInboeken}
            onEditInboeken={startInboekenEdit}
            onStartUitboeken={startUitboeken}
            onOpenRelation={(klantId) => {
              setVaccinationDeepLinkId(null);
              setPage("relaties");
              setDetailId(String(klantId));
            }}
            onOpenFactuur={(factuurId) => {
              // "Open factuur" from the Verblijf factuur card → invoice detail; "Terug" returns to the
              // animal (detailId stays set, so the "animals" page re-renders this AnimalDetail).
              setFactuurReturnTo("animals");
              setFactuurId(String(factuurId));
              setPage("factuur");
            }}
            onOpenNazorg={(inzendingId) => {
              // "Bekijk dossier" op de nazorgtegel: hetzelfde nazorgdossier als vanuit het beheerscherm.
              setNazorgInzendingId(inzendingId);
              setPage("nazorg");
            }}
            openVaccinationId={vaccinationDeepLinkId}
          />
        ) : (
          /* Nothing opened yet on this browser: an empty animal screen with a way to add one, rather
             than a list of every animal in the shelter. Finding an existing animal is what the shell
             search at the top is for. */
          <div className="fawkes-animal-empty">
            <PawPrint size={40} aria-hidden="true" />
            <h2>{t("animals.empty.title")}</h2>
            <p>{t("animals.empty.info")}</p>
            <Button variant="primary" onClick={() => startInboeken(null)}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("animals.empty.action")}
            </Button>
          </div>
        ))}

      {/* De schakelbalk hoort bij alle vier de overzichten, dus hij staat boven de pagina zelf en niet
          binnen één ervan. Elk segment blijft zijn eigen pagina (eigen URL, eigen modulegate). */}
      {isOpvangModulePage(page) && (
        <OpvangModuleBar current={page} onSelect={select} isModuleEnabled={isModuleEnabled} />
      )}


      {page === "opvang-overzichten" && (
        <OpvangOverzichten onOpenAnimal={openAnimalFromOverzicht} />
      )}

      {page === "dieren-aanwezig" && (
        <AnimalPresentView
          onOpenAnimal={(id) => {
            setVaccinationDeepLinkId(null);
            setDetailId(id);
            setPage("animals");
          }}
        />
      )}

      {page === "dieren-geplaatst" && (
        <AnimalPlacedView
          onOpenAnimal={(id) => {
            setVaccinationDeepLinkId(null);
            setDetailId(id);
            setPage("animals");
          }}
        />
      )}

      {page === "pleeggezin-overzicht" && (
        <PleeggezinOverzicht
          onOpenAnimal={(id) => {
            setVaccinationDeepLinkId(null);
            setDetailId(id);
            setPage("animals");
          }}
        />
      )}

      {page === "entingen-overzicht" && (
        <EntingenOverzicht onOpenAnimal={openAnimalFromOverzicht} />
      )}

      {page === "ontwormen-overzicht" && (
        <OntwormenOverzicht onOpenAnimal={openAnimalFromOverzicht} />
      )}

      {page === "medicijn-overzicht" && (
        <MedicijnOverzicht onOpenAnimal={(id) => openAnimalFromOverzicht(id)} />
      )}

      {/* Doorklikken op een nazorgtegel opent de antwoorden, niet het dier: het nazorgdossier komt over
          het beheerscherm heen en "Terug" brengt je op dezelfde plek terug. */}
      {page === "nazorg" && nazorgInzendingId && (
        <NazorgDetail
          inzendingId={nazorgInzendingId}
          onTerug={() => setNazorgInzendingId(null)}
          onOpenDier={(id) => openAnimalFromOverzicht(id)}
        />
      )}

      {page === "nazorg" && !nazorgInzendingId && (
        // nazorg-authoring: questionnaire authoring navigates in-SPA to the settings wizard-builder
        // (replacing the old dead `legacyHref("wizard-beheer")` hand-offs) — a fresh
        // nazorg-vragenlijst, or the builder opened on the clicked definition.
        <NazorgBeheer
          onOpenInzending={(id) => setNazorgInzendingId(id)}
          onNieuweVragenlijst={() => openVragenlijstBuilder({ createNazorg: true })}
          onOpenVragenlijst={(definitionId) => openVragenlijstBuilder({ openDefinitionId: definitionId })}
        />
      )}

      {/* Werkmodus Pension: hetzelfde menu-item "Relatie", maar het klantscherm van een pension —
          de eigen dieren van de klant en hun boekingen, in plaats van de dieren die deze persoon ooit
          bracht en zijn asielrollen. "Alle relatiegegevens" valt terug op het volledige scherm. */}
      {page === "relaties" && werkmodus === "pension" && detailId && (
        <PensionKlant
          // Opnieuw opbouwen bij een ander startdier, zodat een volgende klik dat dier kiest.
          key={`${detailId}:${pensionKlantDier ?? ""}`}
          id={detailId}
          startDierId={pensionKlantDier}
          onBack={() => {
            setPensionKlantDier(null);
            setDetailId(null);
          }}
          onOpenFactuur={openFactuur}
          onNieuweFactuur={nieuweFactuurVoor}
          onOpenDier={openAnimalOpVerblijf}
          onOpenKlant={setDetailId}
          onOpenPlanning={() => setPage("kennelplanning")}
        />
      )}

      {page === "relaties" && !(werkmodus === "pension" && detailId) &&
        (detailId ? (
          <RelatieDetail
            id={detailId}
            onBack={() => setDetailId(null)}
            onOpenFactuur={openFactuur}
            onNieuweFactuur={nieuweFactuurVoor}
            onOpenDier={openAnimalOpVerblijf}
            onNieuweCrematie={(klant) => startCrematieWizard(klant)}
            initialTab={relatieInit?.tab}
            initialDossierId={relatieInit?.dossierId}
          />
        ) : (
          /* Nothing opened yet on this browser (or the remembered relation is gone): an empty relation
             screen with a way to add one — never a list of every relation. Finding an existing one is
             what the shell search at the top is for, exactly like the animal side. */
          <div className="fawkes-animal-empty">
            <UsersIcon size={40} aria-hidden="true" />
            <h2>{t("relaties.empty.title")}</h2>
            <p>{t("relaties.empty.info")}</p>
            <Button variant="primary" onClick={() => setNieuweRelatie(true)}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("relaties.empty.action")}
            </Button>
            {nieuweRelatie && (
              <RelatieCreateDialog
                onClose={() => setNieuweRelatie(false)}
                onCreated={(id) => { setNieuweRelatie(false); setDetailId(id); }}
              />
            )}
          </div>
        ))}

      {page === "financieel-overzichten" && <FactuurOverzicht onOpenFactuur={(id) => openFactuurFromList(String(id))} />}
      {/* Losse verkoop is een factuur zonder debiteur: hetzelfde lege factuurscherm, en pas bij
          Opslaan ontstaat hij. Een eigen kassascherm zou regels, betaling, printen en crediteren
          allemaal opnieuw moeten bouwen (Maarten, 2026-10-23). */}
      {page === "losse-verkoop" && (
        <FactuurDetail
          nieuw={{ klantId: null }}
          onBack={() => setPage("financieel-overzichten")}
          onAangemaakt={(nieuweId) => openFactuur(String(nieuweId))}
          onOpenRelatie={(relatieId) => {
            setDetailId(relatieId);
            setPage("relaties");
          }}
          onOpenDier={(dierId) => openAnimalFromOverzicht(dierId)}
        />
      )}

      {page === "kasboek" && <Kasboek />}
      {page === "dagafsluiting" && <Dagafsluiting />}
      {page === "facturen" && <FactuurList onOpen={openFactuurFromList} />}

      {page === "factuur" && (factuurId || factuurNieuw) && (
        <FactuurDetail
          id={factuurId ?? undefined}
          nieuw={factuurNieuw ?? undefined}
          onBack={leaveFactuur}
          // Bij het eerste Opslaan is hij er echt: vanaf nu gaat het scherm over die factuur, met zijn
          // eigen adres in de balk. Hetzelfde scherm blijft staan, dus je werk blijft waar het was.
          onAangemaakt={(nieuweId) => {
            setFactuurNieuw(null);
            setFactuurId(String(nieuweId));
          }}
          onOpenRelatie={(relatieId) => {
            setDetailId(relatieId);
            setPage("relaties");
          }}
          onOpenDier={(dierId) => openAnimalFromOverzicht(dierId)}
        />
      )}

      {/* Data & Insight: hetzelfde antwoord als achter de zoekbalk, maar met een eigen deur. Het
          scherm praat met dezelfde api — er is geen tweede weg naar de gegevens ontstaan die
          apart bewaakt zou moeten worden. */}
      {page === "schermbouwer" && <SchermbouwerPagina />}
      {page === "schermbouwer-beheer" && <SchermbouwerBeheer />}
      {page === "data-insight" && (
        <DataInsight onOpenDier={(id) => openAnimalFromOverzicht(id)} />
      )}
      {page === "zoekresultaten" && (
        <SearchResults
          query={searchQuery}
          ai={searchAi}
          onOpenHit={openSearchHit}
          onSearchNavigate={navigateSearch}
          onQuickAction={onSearchQuickAction}
          onOpenRoute={openSearchRoute}
          onStartRondleiding={setRondleiding}
        />
      )}

      {page === "uitwisselingen" && (
        <Uitwisselingen
          onReceiveIntake={(animalId, exchangeId, source) =>
            startInboekenFromExchange(animalId, exchangeId, source)
          }
        />
      )}

      {/* Werkmodus Pension: dezelfde menu-ingang "Planning", maar een ander scherm. Een pensionboeking
          staat in pension_reservering met zijn hokindeling in kennel_verblijf; de asielplanning leest
          asiel_verblijf + kennel_asiel_periode en kent die boekingen niet — dit is dus een andere bron,
          geen filter. */}
      {page === "kennelplanning" && werkmodus === "pension" && (
        <PensionPlanning
          onOpenKlant={(klantId) => {
            setDetailId(String(klantId));
            setPage("relaties");
          }}
        />
      )}

      {page === "kennelplanning" && werkmodus !== "pension" && (
        <Kennelplanning
          onOpenAnimal={(dierId) => {
            setVaccinationDeepLinkId(null);
            setDetailId(String(dierId));
            setPage("animals");
          }}
        />
      )}

      {page === "tags-hub" && <TagsHub />}

      {page === "instellingen" && <SettingsDashboard wizardBuilderInit={wizardBuilderInit} />}

      {voorkeurenOpen && <Voorkeuren onClose={() => setVoorkeurenOpen(false)} />}

      {page === "communicatie-react" && <CommunicatieReadView />}

      {page === "advertenties" && <AdvertentiesOverview onOpenAnimal={openAnimalOpAdvertentie} />}

      {page === "under-construction" && (
        <UnderConstruction onHome={() => select("service-accounts")} />
      )}
      </Fragment>

      {/* wizard-dialog-mode: ONE render site per wizard serves both presentations. Launch actions set
          the open flag (dialog over the current page); a deep link / "open as full page" makes the
          wizard page itself current (view). Same tree position + same element either way, so the
          mounted wizard (and its server-side draft) survives the flip. */}
      {(inboekenOpen || page === "inboeken") && (
        <WizardHost
          mode={page === "inboeken" ? "view" : "dialog"}
          onDismiss={() => leaveInboeken(false)}
        >
          <InboekenWizard
            preselectedAnimalId={inboekenAnimalId}
            editVerblijfId={inboekenEditId}
            linkedExchangeId={inboekenExchangeId}
            linkedExchangeSource={inboekenExchangeSource}
            onDone={(animalIds) => leaveInboeken(true, animalIds)}
            onCancel={() => leaveInboeken(false)}
            onExpand={page === "inboeken" ? undefined : () => select("inboeken")}
          />
        </WizardHost>
      )}

      {(crematieWizardOpen || page === "crematie-aanmelding") && (
        <WizardHost
          mode={page === "crematie-aanmelding" ? "view" : "dialog"}
          onDismiss={leaveCrematieWizard}
        >
          <CrematieAanmeldingWizard
            klant={wizardKlant ?? undefined}
            onCancel={leaveCrematieWizard}
            onCreated={(newId, relatieId) => {
              const klant = wizardKlant;
              setWizardKlant(null);
              setCrematieWizardOpen(false);
              // Het dossier hoort onder de klant: daar openen, tabblad Uitvaart, met het nieuwe
              // dossier open. Alleen een aanmelding zonder klant valt terug op het losse scherm.
              const naarKlant = klant?.relatieId ?? relatieId;
              if (naarKlant != null) openKlantUitvaart(naarKlant, newId);
              else openCrematieDossier(newId, "crematies");
            }}
            onExpand={page === "crematie-aanmelding" ? undefined : () => {
              setCrematieWizardOpen(false);
              setPage("crematie-aanmelding");
            }}
          />
        </WizardHost>
      )}

      {(uitboekenOpen || page === "uitboeken") && (
        <WizardHost
          mode={page === "uitboeken" ? "view" : "dialog"}
          onDismiss={() => leaveUitboeken(false)}
        >
          <ReleaseWizard
            preselectedAnimalId={uitboekenAnimalId}
            onDone={(animalIds) => leaveUitboeken(true, animalIds)}
            onCancel={() => leaveUitboeken(false)}
            onExpand={page === "uitboeken" ? undefined : () => select("uitboeken")}
          />
        </WizardHost>
      )}

      {/* De rondleiding hangt buiten elk scherm, want hij brengt je juist van het ene naar het
          andere. Zou hij in het zoekscherm hangen, dan zou hij bij de eerste navigatie verdwijnen —
          precies op het moment dat je hem nodig hebt. */}
      {rondleiding && RONDLEIDINGEN[rondleiding] ? (
        <Rondleiding
          stappen={RONDLEIDINGEN[rondleiding]}
          onSluiten={() => setRondleiding(null)}
          onNavigeer={openSearchRoute}
        />
      ) : null}

      {/* Een document dat wordt nagebouwd als sjabloon: dat duurt minuten, dus je werkt ondertussen
          verder. De melding hangt daarom hier, buiten elk scherm, en brengt je terug als hij klaar is. */}
      {/* Alles wat op de achtergrond doorloopt (document nabouwen, AI-voorstellen) staat gestapeld rechtsonder. */}
      <div className="achtergrond-meldingen">
        <ImportTaakMelding
          t={t}
          onBekijken={() => {
            importTaak.vraagBekijken();
            select("communicatie-react");
          }}
        />
        <AiVoorstelMelding
          store={aiTaakVoorstel}
          prefix="takenAi"
          t={t}
          onBekijken={() => {
            aiTaakVoorstel.vraagBekijken();
            select("instellingen");
          }}
        />
        <AiVoorstelMelding
          store={aiWizardVoorstel}
          prefix="wizardAi"
          t={t}
          onBekijken={() => {
            aiWizardVoorstel.vraagBekijken();
            select("instellingen");
          }}
        />
      </div>
    </AppShell>
  );
}

/**
 * Signed-in shell, gated on the session's forced-change flag: an account whose password an admin set
 * and flagged gets the change screen instead of the app until it replaces it. The flag is set from the
 * login / refresh response and cleared by a successful change, so this re-renders straight into the
 * app the moment that lands — no route, no reload.
 */
function AuthedAppOrForcedPasswordChange() {
  const mustChange = useSyncExternalStore(
    subscribeMustChangePassword,
    getMustChangePassword,
    () => false,
  );
  const { mutate: logout } = useLogout();
  if (mustChange) {
    return <ForcePasswordChangePage onLogout={() => logout()} />;
  }
  return (
    <>
      <TaalVanAccount />
      <AuthedApp />
    </>
  );
}

// jve-login: the auth gate. Refine provides the auth/i18n/data context; <Authenticated> runs the
// authProvider `check` and renders the AppShell only when a token is present, otherwise the Login
// page. Replaces the removed dev-token bypass — an unauthenticated operator now sees a real login.
/** Eén keer gemaakt, buiten de render: een nieuwe client per render gooit alle cache weg. */
const queryClient = maakQueryClient();

export default function App() {
  return (
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      resources={[{ name: "service-accounts" }, { name: "users" }, { name: "wassen-soorten" }, { name: "producten" }, { name: "animals" }, { name: "relaties" }, { name: "vestigingen" }]}
      options={{
        disableTelemetry: true,
        warnWhenUnsavedChanges: false,
        // Eén eigen queryclient, zodat elke geslaagde mutatie het scherm laat bijwerken; zie
        // queryClient.ts voor waarom dat daar hangt en niet per scherm.
        reactQuery: { clientConfig: queryClient },
      }}
    >
      {isSetPasswordPath(globalThis.location?.pathname ?? "/") ? (
        <SetPasswordPage />
      ) : (
        <Inlogpoort fallback={<LoginPage />} loading={<div className="login-main" aria-busy="true" />}>
          <AuthedAppOrForcedPasswordChange />
        </Inlogpoort>
      )}
    </Refine>
  );
}
